import { useState, useEffect, useMemo, useRef } from "react";
import axios from "axios";
import { IoMdClose } from "react-icons/io";
import { FaWhatsapp } from "react-icons/fa";
import { FiEdit, FiTrash2 } from "react-icons/fi";
import toast from "react-hot-toast";
import NotificationBadge from "../components/NotificationBadge";
import type {
  KpiHistoryApiResponse,
  KpiHistoryMetric,
  KpiHistoryResponse,
  KpiHistorySummaryMetric,
  NormalizedKpiHistoryResponse,
} from "../types/kpiHistory";

const MetricCard = ({
  label,
  value,
  icon,
}: {
  label: string;
  value: string;
  icon?: React.ReactNode;
}) => (
  <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100 flex flex-col justify-between min-h-[120px]">
    <div>
      <p className="text-gray-500 text-sm mb-1">{label}</p>
      <h3 className="text-2xl font-bold text-gray-800">{value}</h3>
    </div>
  </div>
);

const CustomSelect = ({
  value,
  onChange,
  options,
  placeholder = "Select an option",
  className = "",
}: {
  value: string;
  onChange: (val: string) => void;
  options: { label: string; value: string; disabled?: boolean }[];
  placeholder?: string;
  className?: string;
}) => {
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const handleClickOutside = () => setIsOpen(false);
    if (isOpen) {
      document.addEventListener("click", handleClickOutside);
    }
    return () => document.removeEventListener("click", handleClickOutside);
  }, [isOpen]);

  const selectedOption = options.find((opt) => opt.value === value);

  return (
    <div
      className={`relative ${className}`}
      onClick={(e) => e.stopPropagation()}
    >
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full bg-white border border-gray-200 py-3 px-4 text-sm text-left flex items-center justify-between outline-none transition-all ${isOpen ? "ring-2 ring-[#008060]/20 border-[#008060] rounded-t-lg" : "rounded-lg hover:border-gray-300"}`}
      >
        <span
          className={
            selectedOption ? "text-gray-800 font-medium" : "text-gray-400"
          }
        >
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        <svg
          className={`w-4 h-4 text-gray-500 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 w-full bg-white border border-t-0 border-[#008060]/20 shadow-lg rounded-b-lg z-50 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-150">
          <ul className="max-h-60 overflow-y-auto custom-scrollbar">
            {options.map((opt) => (
              <li
                key={opt.value}
                onClick={() => {
                  if (opt.disabled) return;
                  onChange(opt.value);
                  setIsOpen(false);
                }}
                aria-disabled={opt.disabled || undefined}
                className={`px-4 py-3 text-sm transition-colors ${
                  opt.disabled
                    ? "cursor-not-allowed text-gray-300"
                    : `cursor-pointer ${value === opt.value ? "bg-[#008060]/5 text-[#008060] font-medium" : "text-gray-700 hover:bg-gray-50"}`
                }`}
              >
                {opt.label}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

const ALL_USERS_PAGE_SIZE = 10;

function normalizeKpiHistoryResponse(
  response: KpiHistoryApiResponse,
): NormalizedKpiHistoryResponse {
  const normalizeMetric = (metric: KpiHistoryMetric) => ({
    achievementPercent: metric.achievementPercent ?? 0,
    actual: metric.actual ?? 0,
    target: metric.target ?? 0,
    variance: metric.variance ?? 0,
  });
  const normalizeSummaryMetric = (metric: KpiHistorySummaryMetric) => ({
    ...metric,
    achievementPercent: metric.achievementPercent ?? 0,
    combinedTarget: metric.combinedTarget ?? 0,
    totalActual: metric.totalActual ?? 0,
  });

  const normalizeRecord = (record: KpiHistoryResponse["records"][number]) => ({
    ...record,
    orders: normalizeMetric(record.orders),
    performance: {
      ...record.performance,
      efficiencyScore: record.performance.efficiencyScore ?? 0,
    },
    registrations: normalizeMetric(record.registrations),
    revenue: normalizeMetric(record.revenue),
  });

  if ("records" in response) {
    return {
      ...response,
      records: response.records.map(normalizeRecord),
      summary: {
        ...response.summary,
        averagePerformance: response.summary.averagePerformance ?? 0,
        orders: normalizeSummaryMetric(response.summary.orders),
        registrations: normalizeSummaryMetric(response.summary.registrations),
        revenue: normalizeSummaryMetric(response.summary.revenue),
      },
    };
  }

  const record = normalizeRecord(response);
  const toSummaryMetric = (metric: typeof record.revenue) => ({
    achievementPercent: metric.achievementPercent,
    combinedTarget: metric.target,
    officersWithTarget: metric.target > 0 ? 1 : 0,
    totalActual: metric.actual,
  });

  return {
    pagination: { page: 1, limit: 1, totalRecords: 1, totalPages: 1 },
    period: response.period,
    records: [record],
    summary: {
      averagePerformance: record.performance.efficiencyScore,
      totalAccountOfficers: 1,
      orders: toSummaryMetric(record.orders),
      registrations: toSummaryMetric(record.registrations),
      revenue: toSummaryMetric(record.revenue),
    },
  };
}

/**
 * Builds query params for /staffs/pending-customers and /staffs/assigned-customers.
 * Backends differ on the param name; axios encodes values when passed as `params`.
 */
function staffsCustomerListParams(
  page: number,
  limit: number,
  search: string,
): Record<string, string | number> {
  const q = String(search || "").trim();
  const params: Record<string, string | number> = { page, limit };
  if (!q) return params;
  params.search = q;
  params.q = q;
  params.query = q;
  params.keyword = q;
  params.term = q;
  params.text = q;
  if (q.includes("@")) {
    params.email = q;
    params.customerEmail = q;
    params.mail = q;
    params.userEmail = q;
  }
  return params;
}

function normalizeStaffStatsPayload(raw: any) {
  const r = raw && typeof raw === "object" ? raw : {};
  const nested =
    r.data && typeof r.data === "object" && !Array.isArray(r.data)
      ? (r.data as Record<string, unknown>)
      : {};
  const pick = (...keys: string[]) => {
    for (const key of keys) {
      const v =
        (nested as any)[key] !== undefined && (nested as any)[key] !== ""
          ? (nested as any)[key]
          : (r as any)[key];
      if (v !== undefined && v !== null && v !== "") {
        const n = Number(v);
        if (Number.isFinite(n)) return n;
      }
    }
    return 0;
  };
  return {
    totalUsers: pick("totalUsers", "total_users", "totalCustomers", "total"),
    assignedUsers: pick(
      "assignedUsers",
      "assigned_users",
      "assignedCustomers",
      "assigned",
      "totalAssigned",
    ),
    unassignedUsers: pick(
      "unassignedUsers",
      "unassigned_users",
      "pending",
      "unassigned",
    ),
    totalActiveOfficers: pick(
      "totalActiveOfficers",
      "total_active_officers",
      "activeOfficers",
    ),
  };
}

function getCustomerDisplayName(user: any): string {
  const direct = String(user?.customerName ?? "").trim();
  if (direct && direct !== "undefined undefined") return direct;
  const fn = String(user?.firstName ?? user?.firstname ?? "").trim();
  const ln = String(user?.lastName ?? user?.lastname ?? "").trim();
  const combined = `${fn} ${ln}`.trim();
  if (combined && combined !== "undefined undefined") return combined;
  const full = String(user?.name ?? user?.fullName ?? "").trim();
  if (full) return full;
  return "N/A";
}

function parseAssignedCustomersListResponse(
  responseData: any,
  responseHeaders?: any,
): { rows: any[]; total: number; totalPages: number | null } {
  const root = responseData ?? {};
  const d = root?.data;

  let rows: any[] = [];
  if (Array.isArray(d)) {
    rows = d;
  } else if (d && typeof d === "object") {
    const keys = [
      "rows",
      "customers",
      "items",
      "users",
      "results",
      "list",
      "records",
    ];
    for (const key of keys) {
      const v = (d as any)[key];
      if (Array.isArray(v)) {
        rows = v;
        break;
      }
    }
  }
  if (!rows.length && Array.isArray(root.customers)) rows = root.customers;
  if (!rows.length && Array.isArray(root.results)) rows = root.results;

  const meta =
    root.pagination ??
    root.meta ??
    (typeof d === "object" && d && !Array.isArray(d)
      ? (d as any).pagination
      : null) ??
    {};

  const dataObjTotal =
    typeof d === "object" && d && !Array.isArray(d)
      ? Number((d as any).total ?? (d as any).count ?? (d as any).totalCount)
      : NaN;

  const totalRaw =
    root.total ??
    root.count ??
    root.totalCount ??
    meta.total ??
    meta.itemCount ??
    (Number.isFinite(dataObjTotal) ? dataObjTotal : undefined);

  let total = Number(totalRaw);
  if (!Number.isFinite(total)) total = 0;

  const tpRaw =
    root.totalPages ??
    meta.totalPages ??
    (typeof d === "object" && d && !Array.isArray(d)
      ? (d as any).totalPages
      : undefined);

  let totalPages: number | null = Number(tpRaw);
  if (!Number.isFinite(totalPages) || totalPages < 1) totalPages = null;

  const hdr = (name: string) => {
    if (!responseHeaders) return undefined;
    const h = responseHeaders as Record<string, string>;
    return (
      h[name] ??
      h[name.toLowerCase()] ??
      (typeof (responseHeaders as any).get === "function"
        ? (responseHeaders as any).get(name)
        : undefined)
    );
  };
  const headerTotal = Number(hdr("x-total-count") ?? hdr("x-total"));
  if (total <= 0 && Number.isFinite(headerTotal) && headerTotal > 0) {
    total = headerTotal;
  }
  const headerPages = Number(hdr("x-total-pages"));
  if (
    (totalPages == null || totalPages < 1) &&
    Number.isFinite(headerPages) &&
    headerPages >= 1
  ) {
    totalPages = headerPages;
  }

  if ((totalPages == null || totalPages < 1) && total > 0) {
    totalPages = Math.max(1, Math.ceil(total / ALL_USERS_PAGE_SIZE));
  }

  return { rows, total, totalPages };
}

type PageListItem = number | "ellipsis";

function buildPageList(current: number, totalPages: number): PageListItem[] {
  if (totalPages <= 1) return [1];
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }
  if (current <= 4) {
    return [1, 2, 3, 4, 5, "ellipsis", totalPages];
  }
  if (current >= totalPages - 3) {
    return [
      1,
      "ellipsis",
      totalPages - 4,
      totalPages - 3,
      totalPages - 2,
      totalPages - 1,
      totalPages,
    ];
  }
  return [
    1,
    "ellipsis",
    current - 1,
    current,
    current + 1,
    "ellipsis",
    totalPages,
  ];
}

const PageNumberPagination = ({
  currentPage,
  totalPages,
  onPageChange,
  disabled,
}: {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  disabled?: boolean;
}) => {
  if (totalPages < 1) return null;
  const items = buildPageList(currentPage, Math.max(1, totalPages));
  return (
    <nav
      className="flex items-center justify-center gap-1 flex-wrap"
      aria-label="Page navigation"
    >
      {items.map((item, idx) =>
        item === "ellipsis" ? (
          <span
            key={`ellipsis-${idx}`}
            className="px-1.5 text-gray-400 text-xs select-none"
            aria-hidden
          >
            …
          </span>
        ) : item === currentPage ? (
          <span
            key={item}
            aria-current="page"
            className="min-w-[2rem] h-8 px-2 rounded-md text-xs font-medium bg-[#008060] text-white border border-[#008060] inline-flex items-center justify-center"
          >
            {item}
          </span>
        ) : (
          <button
            key={item}
            type="button"
            onClick={() => onPageChange(item)}
            disabled={disabled}
            className="min-w-[2rem] h-8 px-2 rounded-md text-xs font-medium border border-gray-200 text-gray-600 hover:bg-gray-50 hover:border-gray-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {item}
          </button>
        ),
      )}
    </nav>
  );
};

const AccountManagement = () => {
  const [activeTab, setActiveTab] = useState<string>("all_users");
  const [activeKPI, setActiveKPI] = useState<"monthly_kpis" | "history">(
    "monthly_kpis",
  );

  const copyToClipboard = (text: string, label: string) => {
    if (!text || text === "N/A" || text === "-") return;
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard`, { id: `copy-${label}` });
  };
  const auth = sessionStorage.getItem("authState");
  const authState = auth && JSON.parse(auth);
  const [showReassignModal, setShowReassignModal] = useState(false);
  const [showMigrateModal, setShowMigrateModal] = useState(false);
  const [selectedPendingUsers, setSelectedPendingUsers] = useState<string[]>(
    [],
  );
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showBulkAssignModal, setShowBulkAssignModal] = useState(false);
  const [pendingCustomers, setPendingCustomers] = useState<any[]>([]);
  const [pendingCustomersLoading, setPendingCustomersLoading] = useState(false);
  const [pendingCustomersError, setPendingCustomersError] = useState("");
  const [pendingTotal, setPendingTotal] = useState(0);
  const [pendingTotalPages, setPendingTotalPages] = useState(1);
  const [assignmentLoading, setAssignmentLoading] = useState(false);
  const [selectedCustomerId, setSelectedCustomerId] = useState<string | null>(
    null,
  );
  const [selectedOfficerId, setSelectedOfficerId] = useState<string>("");
  const [bulkAssignmentLoading, setBulkAssignmentLoading] = useState(false);
  const [bulkSelectedOfficerId, setBulkSelectedOfficerId] =
    useState<string>("");
  const [sourceOfficerId, setSourceOfficerId] = useState<string>("");
  const [targetOfficerId, setTargetOfficerId] = useState<string>("");
  const [migrationLoading, setMigrationLoading] = useState(false);
  const [migrationProgressText, setMigrationProgressText] =
    useState<string>("");
  const [bulkAssignProgressText, setBulkAssignProgressText] =
    useState<string>("");
  const [showAddOfficerModal, setShowAddOfficerModal] = useState(false);
  const [auditLogs, setAuditLogs] = useState<any[]>([]);
  const [auditLogsLoading, setAuditLogsLoading] = useState(false);
  const [auditLogsError, setAuditLogsError] = useState("");
  const [auditDateFilter, setAuditDateFilter] = useState("all");
  const [auditActionFilter, setAuditActionFilter] = useState("all");
  const [officers, setOfficers] = useState<any[]>([]);
  const [officersLoading, setOfficersLoading] = useState(false);
  const [officersError, setOfficersError] = useState("");
  const [officersPage, setOfficersPage] = useState(1);
  const [allUsers, setAllUsers] = useState<any[]>([]);
  const [allUsersLoading, setAllUsersLoading] = useState(false);
  const [allUsersError, setAllUsersError] = useState("");
  const [allUsersPage, setAllUsersPage] = useState(1);
  const [allUsersTotal, setAllUsersTotal] = useState(0);
  const [stats, setStats] = useState({
    totalUsers: 0,
    assignedUsers: 0,
    unassignedUsers: 0,
    totalActiveOfficers: 0,
  });
  const statsRef = useRef(stats);
  useEffect(() => {
    statsRef.current = stats;
  }, [stats]);
  const [statsLoading, setStatsLoading] = useState(false);
  const [showOrderHistoryModal, setShowOrderHistoryModal] = useState(false);
  const [selectedOrderHistoryUser, setSelectedOrderHistoryUser] =
    useState<any>(null);
  const [allUsersSearch, setAllUsersSearch] = useState("");
  const [debouncedAllUsersSearch, setDebouncedAllUsersSearch] = useState("");
  const [pendingSearch, setPendingSearch] = useState("");
  const [debouncedPendingSearch, setDebouncedPendingSearch] = useState("");
  const [pendingPage, setPendingPage] = useState(1);
  const [officersSearch, setOfficersSearch] = useState("");
  const [auditSearch, setAuditSearch] = useState("");
  const [showOfficerDetailsModal, setShowOfficerDetailsModal] = useState(false);
  const [selectedOfficerDetails, setSelectedOfficerDetails] =
    useState<any>(null);
  const [orderHistoryData, setOrderHistoryData] = useState<any[]>([]);
  const [orderHistoryLoading, setOrderHistoryLoading] = useState(false);
  const [officerKpiLoading, setOfficerKpiLoading] = useState(false);

  const today = new Date();
  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];
  const currentMonth = months[today.getMonth()];
  const currentYear = today.getFullYear().toString();

  const [modalKpiMonth, setModalKpiMonth] = useState(currentMonth);
  const [modalKpiYear, setModalKpiYear] = useState(currentYear);

  const [kpiMonth, setKpiMonth] = useState(currentMonth);
  const [kpiYear, setKpiYear] = useState(currentYear);

  const [expectedRevenue, setExpectedRevenue] = useState("");
  const [expectedNewCustomers, setExpectedNewCustomers] = useState("");
  const [expectedCustomersWithOrders, setExpectedCustomersWithOrders] =
    useState("");
  const [expectedTotalOrders, setExpectedTotalOrders] = useState("");
  const [conversionRate, setConversionRate] = useState("");

  const [isRevenueActive, setIsRevenueActive] = useState(true);
  const [isNewCustomersActive, setIsNewCustomersActive] = useState(true);
  const [isTotalOrdersActive, setIsTotalOrdersActive] = useState(true);
  const [isConversionRateActive, setIsConversionRateActive] = useState(false);

  const [kpiOfficerId, setKpiOfficerId] = useState("");
  const [kpiSaving, setKpiSaving] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [kpiList, setKpiList] = useState<any[]>([]);
  const [kpisLoading, setKpisLoading] = useState(false);
  const [kpiHistoryYear, setKpiHistoryYear] = useState(currentYear);
  const [kpiHistoryMonth, setKpiHistoryMonth] = useState(currentMonth);
  const [kpiHistoryOfficerId, setKpiHistoryOfficerId] = useState("all");
  const [kpiHistoryPage, setKpiHistoryPage] = useState(1);
  const [kpiHistoryData, setKpiHistoryData] =
    useState<NormalizedKpiHistoryResponse | null>(null);
  const [kpiHistoryLoading, setKpiHistoryLoading] = useState(false);
  const [kpiHistoryExporting, setKpiHistoryExporting] = useState(false);
  const [selectedKpiOfficer, setSelectedKpiOfficer] = useState<any | null>(
    null,
  );

  const allUsersTotalPages = useMemo(() => {
    if (allUsersTotal > 0) {
      return Math.max(1, Math.ceil(allUsersTotal / ALL_USERS_PAGE_SIZE));
    }
    if (allUsers.length === 0) {
      return Math.max(1, allUsersPage);
    }
    if (allUsers.length < ALL_USERS_PAGE_SIZE) {
      return Math.max(1, allUsersPage);
    }
    return Math.max(allUsersPage + 1, 2);
  }, [allUsersTotal, allUsersPage, allUsers.length]);

  const availableYears = [0, 1, 2, 3].map((offset) =>
    (today.getFullYear() + offset).toString(),
  );
  const historyYears = [0].map((offset) =>
    (today.getFullYear() + offset).toString(),
  );
  const historyMonthOptions = months.map((month, index) => ({
    label: month,
    value: month,
    disabled:
      Number(kpiHistoryYear) > today.getFullYear() ||
      (Number(kpiHistoryYear) === today.getFullYear() &&
        index > today.getMonth()),
  }));
  const availableMonths =
    kpiYear === currentYear ? months.slice(today.getMonth()) : months;

  const officerDetailsYears = [-3, -2, -1, 0].map((offset) =>
    (today.getFullYear() + offset).toString(),
  );
  const officerDetailsMonths =
    modalKpiYear === currentYear
      ? months.slice(0, today.getMonth() + 1)
      : months;

  const handleKpiHistoryYearChange = (year: string) => {
    setKpiHistoryYear(year);
    setKpiHistoryPage(1);
    if (
      Number(year) === today.getFullYear() &&
      months.indexOf(kpiHistoryMonth) > today.getMonth()
    ) {
      setKpiHistoryMonth(currentMonth);
    }
  };

  const handleKpiHistoryMonthChange = (month: string) => {
    setKpiHistoryMonth(month);
    setKpiHistoryPage(1);
  };

  const handleKpiHistoryOfficerChange = (officerId: string) => {
    setKpiHistoryOfficerId(officerId);
    setKpiHistoryPage(1);
  };

  const handleSaveKPIs = async () => {
    if (!kpiOfficerId) {
      toast.error("Please select an Account Officer to set KPIs for.");
      return;
    }

    const isAnyActive =
      isRevenueActive || isNewCustomersActive || isTotalOrdersActive;
    if (!isAnyActive) {
      toast.error("Please enable at least one KPI target.");
      return;
    }

    if (
      (isRevenueActive && !expectedRevenue) ||
      (isNewCustomersActive && !expectedNewCustomers) ||
      (isTotalOrdersActive && !expectedTotalOrders)
    ) {
      toast.error("Please fill in all enabled KPI targets.");
      return;
    }

    setKpiSaving(true);
    try {
      const auth: any = sessionStorage.getItem("authState");
      const jsonData = JSON.parse(auth || "{}");
      const token = jsonData?.token;

      const monthIndex = months.indexOf(kpiMonth) + 1;
      const formattedMonth =
        monthIndex < 10 ? `0${monthIndex}` : `${monthIndex}`;
      const period = `${kpiYear}-${formattedMonth}`;

      const activeOfficers = officers.filter((o: any) => o.status === "active");
      const officerIds =
        kpiOfficerId === "all"
          ? activeOfficers.map((o: any) => o.staffId)
          : [kpiOfficerId];

      for (const officerId of officerIds) {
        const officerName =
          officers.find((o: any) => o.staffId === officerId)?.name || "Unknown";
        const payload = {
          period,
          accountOfficerId: officerId,
          targetRevenue: isRevenueActive ? Number(expectedRevenue) : 0,
          expectedUserRegistrations: isNewCustomersActive
            ? Number(expectedNewCustomers)
            : 0,
          totalExpectedOrders: isTotalOrdersActive
            ? Number(expectedTotalOrders)
            : 0,
          conversionRate: isConversionRateActive ? Number(conversionRate) : 0,
        };

        try {
          if (isEditMode && kpiOfficerId !== "all") {
            await axios.put(
              `${process.env.REACT_APP_API_URL}/admin/kpi`,
              payload,
              { headers: { Authorization: `Bearer ${token}` } },
            );
            if (kpiOfficerId !== "all")
              toast.success(
                `KPIs for ${kpiMonth} ${kpiYear} updated for ${officerName}`,
              );
          } else {
            await axios.post(
              `${process.env.REACT_APP_API_URL}/admin/kpi`,
              payload,
              { headers: { Authorization: `Bearer ${token}` } },
            );
            if (kpiOfficerId !== "all")
              toast.success(
                `KPIs for ${kpiMonth} ${kpiYear} saved for ${officerName}`,
              );
          }
        } catch (err: any) {
          console.error(`Failed to save KPI for ${officerName}:`, err);
          toast.error(`Failed to save KPIs for ${officerName}`);
        }
      }

      if (kpiOfficerId === "all") {
        toast.success("KPI targets set for all account officers");
      }

      // fetchKPIList();

      setExpectedRevenue("");
      setExpectedNewCustomers("");
      setExpectedCustomersWithOrders("");
      setExpectedTotalOrders("");
      setConversionRate("");
      setKpiOfficerId("");
      setIsRevenueActive(true);
      setIsNewCustomersActive(true);
      setIsTotalOrdersActive(true);
      setIsConversionRateActive(true);
      setIsEditMode(false);
    } catch (error: any) {
      console.error("Error saving KPIs:", error);
      toast.error(
        error.response?.data?.message || error.message || "Failed to save KPIs",
      );
    } finally {
      setKpiSaving(false);
    }
  };

  // const fetchKPIList = async () => {
  //   setKpisLoading(true);
  //   try {
  //     const auth: any = sessionStorage.getItem("authState");
  //     const jsonData = JSON.parse(auth || "{}");
  //     const token = jsonData?.token;

  //     const res = await axios.get(
  //       `${process.env.REACT_APP_API_URL}/admin/kpi`,
  //       { headers: { Authorization: `Bearer ${token}` } },
  //     );

  //     const data = res.data?.data || res.data || [];
  //     if (Array.isArray(data)) {
  //       setKpiList(data);
  //     } else if (data && typeof data === "object") {
  //       // API may return a single KPI object instead of an array
  //       setKpiList([data]);
  //     }
  //   } catch (err) {
  //     console.error("Failed to fetch general KPIs list", err);
  //   } finally {
  //     setKpisLoading(false);
  //   }
  // };

  const getKpiPerformanceStatus = (
    actual: number,
    target: number,
    isCurrentPeriod: boolean,
  ) => {
    if (isCurrentPeriod) return "In Progress";
    if (actual === target) return "Good";
    if (actual > target) return "Excellent";

    return "Below Target";
  };

  const historyRows = useMemo(() => {
    const records = kpiHistoryData?.records ?? [];
    const officerId = selectedKpiOfficer?.staffId || kpiHistoryOfficerId;

    return records
      .filter(
        (record) =>
          officerId === "all" || record.accountOfficerId === officerId,
      )
      .map((record) => ({
        ...record,
        staffId: record.accountOfficerId,
        name: record.accountOfficerName,
        status: record.accountOfficerStatus,
        selectedPeriod: kpiHistoryData?.period.period,
        revenueTarget: record.revenue.target,
        actualRevenue: record.revenue.actual,
        revenuePerformance: record.revenue.achievementPercent,
        registrationTarget: record.registrations.target,
        actualRegistrations: record.registrations.actual,
        registrationPerformance: record.registrations.achievementPercent,
        orderTarget: record.orders.target,
        actualOrders: record.orders.actual,
        orderPerformance: record.orders.achievementPercent,
        efficiency: record.performance.efficiencyScore,
        overallPerformance: record.performance.efficiencyScore,
      }));
  }, [kpiHistoryData, kpiHistoryOfficerId, selectedKpiOfficer?.staffId]);

  const selectedOfficerHistoryData = useMemo(() => {
    if (!selectedKpiOfficer) return null;
    return (
      historyRows.find((row) => row.staffId === selectedKpiOfficer.staffId) ||
      selectedKpiOfficer
    );
  }, [historyRows, selectedKpiOfficer]);

  const handleOpenOfficerKpiHistory = (officer: any) => {
    setSelectedKpiOfficer(officer);
    setActiveTab("kpis");
    setActiveKPI("history");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleExportKpiHistory = async (accountOfficerId: string) => {
    const month = String(months.indexOf(kpiHistoryMonth) + 1).padStart(2, "0");
    setKpiHistoryExporting(true);

    try {
      const authState = JSON.parse(sessionStorage.getItem("authState") || "{}");
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/admin/kpi-history/history/export`,
        {
          headers: { Authorization: `Bearer ${authState?.token}` },
          params: {
            year: kpiHistoryYear,
            month,
            accountOfficerId,
          },
          responseType: "blob",
        },
      );

      const downloadUrl = URL.createObjectURL(response.data);
      const downloadLink = document.createElement("a");
      downloadLink.href = downloadUrl;
      downloadLink.download = `kpi-history-${accountOfficerId}-${kpiHistoryYear}-${month}.xlsx`;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      downloadLink.remove();
      URL.revokeObjectURL(downloadUrl);
    } catch (error: any) {
      toast.error(
        error.response?.data?.message || "Failed to export KPI history",
      );
    } finally {
      setKpiHistoryExporting(false);
    }
  };

  const handleDeleteKPI = async (period: string, officerId: string) => {
    if (
      !window.confirm(`Are you sure you want to delete the KPI for ${period}?`)
    )
      return;

    try {
      const auth: any = sessionStorage.getItem("authState");
      const jsonData = JSON.parse(auth || "{}");
      const token = jsonData?.token;

      await axios.delete(
        `${process.env.REACT_APP_API_URL}/admin/kpi?period=${period}&accountOfficerId=${officerId}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );

      // await fetchKPIList();
      toast.success("KPI deleted successfully");
    } catch (error: any) {
      console.error("Error deleting KPI:", error);
      toast.error(error.response?.data?.message || "Failed to delete KPI");
    }
  };

  useEffect(() => {
    if (activeTab !== "kpis" || activeKPI !== "history") return;

    const accountOfficerId = selectedKpiOfficer?.staffId || kpiHistoryOfficerId;
    const month = String(months.indexOf(kpiHistoryMonth) + 1).padStart(2, "0");
    const authState = JSON.parse(sessionStorage.getItem("authState") || "{}");
    let isCurrentRequest = true;
    setKpiHistoryLoading(true);

    axios
      .get<{ data: KpiHistoryApiResponse }>(
        `${process.env.REACT_APP_API_URL}/admin/kpi-history/history`,
        {
          headers: { Authorization: `Bearer ${authState?.token}` },
          params: {
            year: kpiHistoryYear,
            month,
            accountOfficerId,
            page: kpiHistoryPage,
            limit: 10,
          },
        },
      )
      .then((response) => {
        if (!isCurrentRequest) return;
        const normalizedResponse = normalizeKpiHistoryResponse(
          response.data.data,
        );
        setKpiHistoryData(normalizedResponse);
      })
      .catch((error) => {
        if (!isCurrentRequest) return;
        toast.error(
          error.response?.data?.error ||
            error.response?.data?.message ||
            "Failed to fetch KPI history data",
        );
      })
      .finally(() => {
        if (isCurrentRequest) setKpiHistoryLoading(false);
      });

    return () => {
      isCurrentRequest = false;
    };
  }, [
    activeTab,
    activeKPI,
    selectedKpiOfficer?.staffId,
    kpiHistoryOfficerId,
    kpiHistoryYear,
    kpiHistoryMonth,
    kpiHistoryPage,
  ]);

  useEffect(() => {
    const fetchKPIsForForm = async () => {
      if (!kpiOfficerId || kpiOfficerId === "all" || !kpiMonth || !kpiYear) {
        setExpectedRevenue("");
        setExpectedNewCustomers("");
        setExpectedTotalOrders("");
        setConversionRate("");
        setIsRevenueActive(true);
        setIsNewCustomersActive(true);
        setIsTotalOrdersActive(true);
        setIsConversionRateActive(true);
        setIsEditMode(false);
        return;
      }

      try {
        const monthIndex = months.indexOf(kpiMonth) + 1;
        const formattedMonth =
          monthIndex < 10 ? `0${monthIndex}` : `${monthIndex}`;
        const period = `${kpiYear}-${formattedMonth}`;

        const auth: any = sessionStorage.getItem("authState");
        const jsonData = JSON.parse(auth || "{}");
        const token = jsonData?.token;

        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/admin/kpi?accountOfficerId=${kpiOfficerId}&period=${period}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );

        const data = res.data?.data || res.data;
        const fetchedKPI = Array.isArray(data) ? data[0] : data;

        const rev = fetchedKPI?.targetRevenue || 0;
        const reg = fetchedKPI?.expectedUserRegistrations || 0;
        const ord = fetchedKPI?.totalExpectedOrders || 0;
        const conv = fetchedKPI?.conversionRate || 0;

        const hasActualTargets = rev > 0 || reg > 0 || ord > 0 || conv > 0;

        if (fetchedKPI && hasActualTargets) {
          setExpectedRevenue(rev > 0 ? rev.toString() : "");
          setExpectedNewCustomers(reg > 0 ? reg.toString() : "");
          setExpectedTotalOrders(ord > 0 ? ord.toString() : "");
          setConversionRate(conv > 0 ? conv.toString() : "");

          setIsRevenueActive(rev > 0);
          setIsNewCustomersActive(reg > 0);
          setIsTotalOrdersActive(ord > 0);
          setIsConversionRateActive(conv > 0);
          setIsEditMode(true);
        } else {
          setExpectedRevenue("");
          setExpectedNewCustomers("");
          setExpectedTotalOrders("");
          setConversionRate("");
          setIsRevenueActive(true);
          setIsNewCustomersActive(true);
          setIsTotalOrdersActive(true);
          setIsConversionRateActive(true);
          setIsEditMode(false);
        }
      } catch (err: any) {
        console.error("Failed to fetch specific KPI for form", err);
        setExpectedRevenue("");
        setExpectedNewCustomers("");
        setExpectedTotalOrders("");
        setConversionRate("");
        setIsRevenueActive(true);
        setIsNewCustomersActive(true);
        setIsTotalOrdersActive(true);
        setIsConversionRateActive(true);
        setIsEditMode(false);
      }
    };

    fetchKPIsForForm();
  }, [kpiMonth, kpiYear, kpiOfficerId]);

  useEffect(() => {
    const fetchOfficerModalKPI = async () => {
      if (!selectedOfficerDetails || !showOfficerDetailsModal) return;

      setOfficerKpiLoading(true);
      try {
        const monthIndex = months.indexOf(modalKpiMonth) + 1;
        const formattedMonth =
          monthIndex < 10 ? `0${monthIndex}` : `${monthIndex}`;
        const period = `${modalKpiYear}-${formattedMonth}`;

        const auth: any = sessionStorage.getItem("authState");
        const jsonData = JSON.parse(auth || "{}");
        const token = jsonData?.token;

        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/staffs/${selectedOfficerDetails.staffId}/kpi?period=${period}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );

        const root = res.data;
        const officerData = root?.data ?? root;

        const normalizedOfficer =
          officerData && typeof officerData === "object"
            ? {
                ...(officerData.fullName
                  ? { name: officerData.fullName }
                  : null),
                ...(officerData.assignedCustomers != null
                  ? { assignedCustomersCount: officerData.assignedCustomers }
                  : null),
              }
            : {};

        const kpi = officerData?.kpi ?? null;
        const hasTargets = Boolean(
          kpi &&
          ((kpi.targetRevenue ?? 0) > 0 ||
            (kpi.expectedUserRegistrations ?? 0) > 0 ||
            (kpi.totalExpectedOrders ?? 0) > 0 ||
            (kpi.conversionRate ?? 0) > 0),
        );

        setSelectedOfficerDetails((prev: any) => ({
          ...prev,
          ...(officerData && typeof officerData === "object"
            ? officerData
            : null),
          ...normalizedOfficer,
          kpi: hasTargets ? kpi : null,
        }));
      } catch (err) {
        console.error("Failed to fetch specific KPI for modal", err);
        setSelectedOfficerDetails((prev: any) => ({ ...prev, kpi: null }));
      } finally {
        setOfficerKpiLoading(false);
      }
    };

    fetchOfficerModalKPI();
  }, [modalKpiMonth, modalKpiYear, selectedOfficerDetails?.staffId]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedAllUsersSearch(allUsersSearch);
    }, 500);
    return () => clearTimeout(timer);
  }, [allUsersSearch]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedPendingSearch(pendingSearch);
    }, 500);
    return () => clearTimeout(timer);
  }, [pendingSearch]);

  useEffect(() => {
    setOfficersPage(1);
  }, [officersSearch]);

  const fetchOrderHistory = async (
    customerId: string,
    customerEmail?: string,
  ) => {
    setOrderHistoryLoading(true);
    setOrderHistoryData([]);
    try {
      const auth: any = sessionStorage.getItem("authState");
      const jsonData = JSON.parse(auth || "{}");
      const token = jsonData?.token;

      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/getAllOrderHistory`,
        { headers: { Authorization: token } },
      );

      const allHistory = res.data;
      let userOrders: any[] = [];

      if (Array.isArray(allHistory)) {
        const userBlock = allHistory.find(
          (item: any) =>
            (item._id && item._id === customerId) ||
            (customerEmail &&
              item.email?.toLowerCase() === customerEmail.toLowerCase()),
        );

        if (userBlock && userBlock.orders) {
          userOrders = userBlock.orders;
        }
      }

      setOrderHistoryData(userOrders);
    } catch (err: any) {
      console.error("Failed to fetch order history:", err);
      toast.error(
        err.response?.data?.message || "Failed to fetch order history",
      );
    } finally {
      setOrderHistoryLoading(false);
    }
  };

  useEffect(() => {
    const fetchAuditLogs = async () => {
      setAuditLogsLoading(true);
      setAuditLogsError("");
      try {
        const auth: any = sessionStorage.getItem("authState");
        const jsonData = JSON.parse(auth || "{}");
        const token = jsonData?.token;
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/staffs/audit-logs`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );
        setAuditLogs(response.data?.data || response.data || []);
      } catch (error: any) {
        console.error("Error fetching audit logs:", error);
        setAuditLogsError(
          error.response?.data?.message ||
            error.message ||
            "Failed to fetch audit logs",
        );
      } finally {
        setAuditLogsLoading(false);
      }
    };
    fetchAuditLogs();
  }, []);

  const fetchStats = async () => {
    setStatsLoading(true);
    try {
      const auth: any = sessionStorage.getItem("authState");
      const jsonData = JSON.parse(auth || "{}");
      const token = jsonData?.token;
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/staffs/stats`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      setStats(normalizeStaffStatsPayload(response.data));
    } catch (error) {
      console.error("Error fetching stats:", error);
    } finally {
      setStatsLoading(false);
    }
  };

  const fetchOfficers = async () => {
    setOfficersLoading(true);
    setOfficersError("");
    try {
      const auth: any = sessionStorage.getItem("authState");
      const jsonData = JSON.parse(auth || "{}");
      const token = jsonData?.token;
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/staffs/all`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          params: {
            limit: 5000,
          },
        },
      );
      const responseData = response.data;
      const items = responseData?.data || responseData || [];
      setOfficers(items);
    } catch (error: any) {
      console.error("Error fetching officers:", error);
      setOfficersError(
        error.response?.data?.message ||
          error.message ||
          "Failed to fetch account officers",
      );
    } finally {
      setOfficersLoading(false);
    }
  };

  const fetchPendingCustomers = async (page = 1, search = "") => {
    setPendingCustomersLoading(true);
    setPendingCustomersError("");
    try {
      const auth: any = sessionStorage.getItem("authState");
      const jsonData = JSON.parse(auth || "{}");
      const token = jsonData?.token;
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/staffs/pending-customers`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          params: staffsCustomerListParams(page, 10, search),
        },
      );
      const responseData = response.data;
      setPendingCustomers(responseData?.data || []);
      setPendingTotal(responseData?.total || 0);
      setPendingTotalPages(responseData?.totalPages || 1);
      setPendingPage(page);
    } catch (error: any) {
      console.error("Error fetching pending customers:", error);
      setPendingCustomersError(
        error.response?.data?.message ||
          error.message ||
          "Failed to fetch pending customers",
      );
    } finally {
      setPendingCustomersLoading(false);
    }
  };

  const fetchAllUsers = async (page = 1, search = "") => {
    setAllUsersLoading(true);
    setAllUsersError("");
    try {
      const auth: any = sessionStorage.getItem("authState");
      const jsonData = JSON.parse(auth || "{}");
      const token = jsonData?.token;
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/staffs/assigned-customers`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          params: staffsCustomerListParams(page, 10, search),
        },
      );
      const parsed = parseAssignedCustomersListResponse(
        response.data,
        response.headers,
      );
      let { rows, total } = parsed;
      const searchTrim = String(search || "").trim();
      if (!searchTrim) {
        const assigned = Number(statsRef.current?.assignedUsers ?? 0);
        if (assigned > 0) {
          total = Math.max(total, assigned);
        }
      }
      setAllUsers(rows);
      setAllUsersTotal(total);
      setAllUsersPage(page);
    } catch (error: any) {
      console.error("Error fetching all users:", error);
      setAllUsersError(
        error.response?.data?.message ||
          error.message ||
          "Failed to fetch users",
      );
    } finally {
      setAllUsersLoading(false);
    }
  };

  useEffect(() => {
    fetchOfficers();
    fetchStats();
  }, []);

  useEffect(() => {
    fetchPendingCustomers(1, debouncedPendingSearch);
  }, [debouncedPendingSearch]);

  useEffect(() => {
    fetchAllUsers(1, debouncedAllUsersSearch);
  }, [debouncedAllUsersSearch]);

  useEffect(() => {
    if (debouncedAllUsersSearch.trim()) return;
    if (stats.assignedUsers <= 0) return;
    setAllUsersTotal((prev) => Math.max(prev, stats.assignedUsers));
  }, [stats.assignedUsers, debouncedAllUsersSearch]);

  const handleReassignCustomer = async () => {
    if (!selectedCustomerId || !selectedOfficerId) return;
    setAssignmentLoading(true);
    try {
      const auth: any = sessionStorage.getItem("authState");
      const jsonData = JSON.parse(auth || "{}");
      const token = jsonData?.token;

      await axios.post(
        `${process.env.REACT_APP_API_URL}/staffs/bulk-assign`,
        {
          officerId: selectedOfficerId,
          customerIds: [selectedCustomerId],
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      setShowReassignModal(false);
      setAssignmentLoading(false);
      setSelectedCustomerId(null);
      setSelectedOfficerId("");
      fetchAllUsers(1, debouncedAllUsersSearch);
      fetchOfficers(); // Refresh stats
      fetchStats();
      fetchPendingCustomers(1, debouncedPendingSearch);
      toast.success("Customer reassigned successfully");
    } catch (error: any) {
      console.error("Error reassigning customer:", error);
      toast.error(
        error.response?.data?.message ||
          error.message ||
          "Failed to reassign customer",
      );
      setAssignmentLoading(false);
    }
  };

  const handleAssignCustomer = async () => {
    if (!selectedCustomerId || !selectedOfficerId) return;
    setAssignmentLoading(true);
    try {
      const auth: any = sessionStorage.getItem("authState");
      const jsonData = JSON.parse(auth || "{}");
      const token = jsonData?.token;

      const customerEmail =
        pendingCustomers.find((u: any) => u.customerId === selectedCustomerId)
          ?.customerEmail || "";

      await axios.post(
        `${process.env.REACT_APP_API_URL}/staffs/bulk-assign`,
        {
          officerId: selectedOfficerId,
          customerIds: [selectedCustomerId],
          customerEmails: [customerEmail],
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      setShowAssignModal(false);
      setSelectedCustomerId(null);
      setSelectedOfficerId("");
      fetchPendingCustomers(1, debouncedPendingSearch);
      fetchOfficers(); // Refresh officers stats if needed
      fetchStats();
      fetchAllUsers(1, debouncedAllUsersSearch);
      toast.success("Customer assigned successfully");
    } catch (error: any) {
      console.error("Error assigning customer:", error);
      toast.error(
        error.response?.data?.message ||
          error.message ||
          "Failed to assign customer",
      );
    } finally {
      setAssignmentLoading(false);
    }
  };

  const handleBulkAssign = async () => {
    if (!bulkSelectedOfficerId || selectedPendingUsers.length === 0) return;
    setBulkAssignmentLoading(true);
    setBulkAssignProgressText("");
    try {
      const auth: any = sessionStorage.getItem("authState");
      const jsonData = JSON.parse(auth || "{}");
      const token = jsonData?.token;

      const totalUsers = selectedPendingUsers.length;
      const BATCH_SIZE = 50;
      let successCount = 0;

      for (let i = 0; i < totalUsers; i += BATCH_SIZE) {
        const chunkIds = selectedPendingUsers.slice(i, i + BATCH_SIZE);
        const chunkEmails = chunkIds.map((id) => {
          const user = pendingCustomers.find((u: any) => u.customerId === id);
          return user?.customerEmail || "";
        });

        const currentBatchNumber = Math.floor(i / BATCH_SIZE) + 1;
        const totalBatches = Math.ceil(totalUsers / BATCH_SIZE);

        setBulkAssignProgressText(
          `Assigning batch ${currentBatchNumber} of ${totalBatches} (${Math.min(i + BATCH_SIZE, totalUsers)} of ${totalUsers} customers)...`,
        );

        await axios.post(
          `${process.env.REACT_APP_API_URL}/staffs/bulk-assign`,
          {
            officerId: bulkSelectedOfficerId,
            customerIds: chunkIds,
            customerEmails: chunkEmails,
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );

        successCount += chunkIds.length;
      }

      setShowBulkAssignModal(false);
      setBulkSelectedOfficerId("");
      setSelectedPendingUsers([]); // Clear selection
      fetchPendingCustomers(1, debouncedPendingSearch);
      fetchOfficers(); // Refresh stats
      fetchStats();
      fetchAllUsers(1, debouncedAllUsersSearch);
      toast.success(`Successfully assigned ${successCount} customers.`);
    } catch (error: any) {
      console.error("Error bulk assigning:", error);
      toast.error(
        error.response?.data?.message ||
          error.message ||
          "Failed to bulk assign customers",
      );
      // Refresh table even on partial success so user sees updated assignments
      fetchPendingCustomers(1, debouncedPendingSearch);
      fetchOfficers();
      fetchStats();
      fetchAllUsers(1, debouncedAllUsersSearch);
    } finally {
      setBulkAssignmentLoading(false);
      setBulkAssignProgressText("");
    }
  };

  const filteredAuditLogs = auditLogs
    .filter((log: any) => {
      if (auditActionFilter !== "all" && log.actionType !== auditActionFilter)
        return false;

      if (auditDateFilter === "all") return true;

      const logDate = new Date(log.createdAt);
      const now = new Date();
      const startOfToday = new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate(),
      );

      if (auditDateFilter === "today") {
        return logDate >= startOfToday;
      }
      if (auditDateFilter === "this_week") {
        const startOfWeek = new Date(startOfToday);
        startOfWeek.setDate(startOfWeek.getDate() - startOfWeek.getDay());
        return logDate >= startOfWeek;
      }
      if (auditDateFilter === "this_month") {
        const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
        return logDate >= startOfMonth;
      }
      if (auditDateFilter === "last_month") {
        const startOfLastMonth = new Date(
          now.getFullYear(),
          now.getMonth() - 1,
          1,
        );
        const startOfThisMonth = new Date(now.getFullYear(), now.getMonth(), 1);
        return logDate >= startOfLastMonth && logDate < startOfThisMonth;
      }
      return true;
    })
    .filter((log: any) => {
      if (!auditSearch) return true;
      const search = auditSearch.toLowerCase();
      return (
        log.customerName?.toLowerCase().includes(search) ||
        log.accountOfficerName?.toLowerCase().includes(search) ||
        log.performedBy?.toLowerCase().includes(search) ||
        (log.actionType || "").replace(/_/g, " ").toLowerCase().includes(search)
      );
    });

  const filteredOfficersList = officers.filter((officer: any) => {
    if (!officersSearch) return true;
    const search = officersSearch.toLowerCase();
    return (
      officer.name?.toLowerCase().includes(search) ||
      (officer.officialEmail || officer.email)
        ?.toLowerCase()
        .includes(search) ||
      officer.phoneNumber?.includes(search) ||
      officer.referralCode?.toLowerCase().includes(search)
    );
  });

  const computedOfficersTotal = filteredOfficersList.length;
  const computedOfficersTotalPages = Math.ceil(computedOfficersTotal / 10) || 1;

  const ITEMS_PER_PAGE = 10;
  const paginatedOfficersList = filteredOfficersList.slice(
    (officersPage - 1) * ITEMS_PER_PAGE,
    officersPage * ITEMS_PER_PAGE,
  );

  const togglePendingUser = (id: string) => {
    if (selectedPendingUsers.includes(id)) {
      setSelectedPendingUsers(
        selectedPendingUsers.filter((userId) => userId !== id),
      );
    } else {
      setSelectedPendingUsers([...selectedPendingUsers, id]);
    }
  };

  const toggleAllPendingUsers = () => {
    if (
      selectedPendingUsers.length === pendingCustomers.length &&
      pendingCustomers.length > 0
    ) {
      setSelectedPendingUsers([]);
    } else {
      setSelectedPendingUsers(pendingCustomers.map((u) => u.customerId));
    }
  };

  if (selectedOfficerHistoryData) {
    const officer = selectedOfficerHistoryData;
    const detailPeriodLabel = `${kpiHistoryMonth} ${kpiHistoryYear}`;

    const isCurrentKpiHistoryPeriod =
      Number(kpiHistoryYear) === today.getFullYear() &&
      months.indexOf(kpiHistoryMonth) === today.getMonth();

    const detailPeriodStatus = isCurrentKpiHistoryPeriod
      ? "In Progress"
      : "Completed";

    const detailRows = [
      {
        label: "Monthly Revenue",
        target: officer.revenueTarget || 0,
        actual: officer.actualRevenue || 0,
        performance: officer.revenuePerformance || 0,
        suffix: "",
        prefix: "₦",
      },
      {
        label: "User Registrations",
        target: officer.registrationTarget || 0,
        actual: officer.actualRegistrations || 0,
        performance: officer.registrationPerformance || 0,
        suffix: "",
      },
      {
        label: "Total Orders",
        target: officer.orderTarget || 0,
        actual: officer.actualOrders || 0,
        performance: officer.orderPerformance || 0,
        suffix: "",
      },
      {
        label: "Efficiency Score",
        target: 100,
        actual: officer.efficiency || 0,
        performance: officer.efficiency || 0,
        suffix: "%",
      },
    ];

    return (
      <main className="w-full">
        <div className="mb-8 flex items-start justify-between gap-4">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900">
              Account Management
            </h1>
            <p className="text-gray-500 text-xs sm:text-sm">
              Manage platform news and announcements
            </p>
          </div>

          <div className="flex items-center gap-2 text-xs text-gray-500 whitespace-nowrap">
            <span>Home</span>
            <span>›</span>
            <span>Account Management</span>
            <span>›</span>
            <span>KPIs</span>
            <span>›</span>
            <span className="text-gray-700 font-medium">KPI History</span>
            <span>›</span>
            <span className="text-gray-700 font-medium">
              Account Officer Details
            </span>
          </div>
        </div>

        <div className="mb-5">
          <button
            type="button"
            onClick={() => {
              setSelectedKpiOfficer(null);
              setActiveKPI("history");
            }}
            className="inline-flex items-center gap-2 text-sm font-medium text-gray-700 hover:text-[#008060] transition-colors"
          >
            <span aria-hidden>←</span>
            Back to KPI History
          </button>
        </div>

        {kpiHistoryLoading ? (
          <div
            role="status"
            aria-live="polite"
            className="mb-4 flex items-center gap-3 text-sm text-gray-500"
          >
            <span className="h-5 w-5 animate-spin rounded-full border-2 border-gray-200 border-t-[#008060]" />
            Loading KPI history...
          </div>
        ) : (
          <section className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4 border-b border-gray-100 px-6 py-5">
              <div className="flex items-center gap-4">
                <div className="flex h-16 w-16 items-center justify-center rounded-full bg-[#eaf7f3] text-2xl font-bold text-[#008060]">
                  {officer.name?.charAt(0)?.toUpperCase() || "A"}
                </div>

                <div>
                  <div className="flex items-center gap-3 flex-wrap">
                    <h2 className="text-2xl font-bold text-gray-900">
                      {officer.name}
                    </h2>
                    <span className="inline-flex items-center gap-1 rounded-full bg-[#eaf7f3] px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider text-[#008060]">
                      Account Officer
                    </span>
                  </div>

                  <div className="mt-1 flex flex-wrap items-center gap-3 text-sm text-gray-500">
                    <span>Staff ID: {officer.staffId || "N/A"}</span>
                    <span className="text-gray-300">|</span>
                    <span>Joined: {officer.joinedAt || "N/A"}</span>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <div
                  className={`inline-flex items-center gap-2 rounded-xl border px-3 py-2 text-sm font-semibold ${
                    isCurrentKpiHistoryPeriod
                      ? "border-amber-200 bg-amber-50 text-amber-700"
                      : "border-[#d7efe7] bg-[#eaf7f3] text-[#008060]"
                  }`}
                >
                  <span
                    className={`inline-block h-2.5 w-2.5 rounded-full ${
                      isCurrentKpiHistoryPeriod
                        ? "bg-amber-500"
                        : "bg-[#008060]"
                    }`}
                  />
                  {detailPeriodLabel} {detailPeriodStatus}
                </div>

                <div className="flex flex-row gap-2">
                  <CustomSelect
                    value={kpiHistoryMonth}
                    onChange={setKpiHistoryMonth}
                    options={historyMonthOptions}
                  />

                  <CustomSelect
                    value={kpiYear}
                    onChange={(val) => {
                      setKpiYear(val);
                      if (
                        val === currentYear &&
                        months.indexOf(kpiMonth) < today.getMonth()
                      ) {
                        setKpiMonth(currentMonth);
                      }
                    }}
                    options={availableYears.map((y) => ({
                      label: y,
                      value: y,
                    }))}
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 p-6">
              {[
                {
                  label: "Monthly Revenue",
                  value: `₦${(officer.actualRevenue ?? "-").toLocaleString()}`,
                  target:
                    officer.revenueTarget > 0
                      ? `Target: ₦${officer.revenueTarget.toLocaleString()}`
                      : "No Target Set",
                  percent: officer.revenuePerformance ?? "-",
                  accent: "green",
                  icon: "₦",
                },
                {
                  label: "User Registrations",
                  value: (officer.actualRegistrations ?? "-").toLocaleString(),
                  target:
                    officer.registrationTarget > 0
                      ? `Target: ${(officer.registrationTarget ?? "-").toLocaleString()}`
                      : "No Target Set",
                  percent: officer.registrationPerformance ?? "-",
                  accent: "purple",
                  icon: "◉",
                },
                {
                  label: "Total Orders",
                  value: (officer.actualOrders ?? "-").toLocaleString(),
                  target: officer.orderTarget
                    ? `Target: ${(officer.orderTarget ?? "-").toLocaleString()}`
                    : "No Target Set",
                  percent: officer.orderPerformance ?? "-",
                  accent: "amber",
                  icon: "▣",
                },
                {
                  label: "Efficiency Score",
                  value: officer?.efficiency ? `${officer.efficiency}%` : "-",
                  target: "Performance",
                  percent: officer.efficiency ?? "-",
                  accent: "teal",
                  icon: "↗",
                },
              ].map((metric) => (
                <div
                  key={metric.label}
                  className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm"
                >
                  <div className="mb-4 flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-600">
                      {metric.label}
                    </span>
                    <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#f0faf7] text-[#008060] text-sm font-bold">
                      {metric.icon}
                    </span>
                  </div>

                  <div className="mb-2 text-3xl font-bold text-gray-900 leading-none">
                    {metric.value}
                  </div>

                  <p className="mb-3 text-xs text-gray-500">{metric.target}</p>

                  <div className="h-2.5 w-full overflow-hidden rounded-full bg-gray-100">
                    <div
                      className={`h-full rounded-full ${
                        metric.accent === "green"
                          ? "bg-[#008060]"
                          : metric.accent === "purple"
                            ? "bg-[#8b5cf6]"
                            : metric.accent === "amber"
                              ? "bg-[#fbbf24]"
                              : "bg-[#14b8a6]"
                      }`}
                      style={{
                        width: `${Math.min(100, metric.percent || 0)}%`,
                      }}
                    />
                  </div>

                  <div className="mt-2 text-right text-sm font-semibold text-gray-700">
                    {metric.percent || 0}% achieved so far
                  </div>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-[1.6fr_0.9fr] gap-6 p-6 pt-0">
              <div className="rounded-2xl border border-gray-100 bg-white overflow-hidden">
                <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
                  <h3 className="text-base font-bold text-gray-900">
                    KPI vs Actual Summary
                  </h3>
                  <button
                    type="button"
                    onClick={() => handleExportKpiHistory(officer.staffId)}
                    disabled={kpiHistoryExporting}
                    className="rounded-lg border border-[#008060] bg-white px-3 py-1.5 text-sm font-medium text-[#008060] hover:bg-[#eaf7f3] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {kpiHistoryExporting ? "Exporting..." : "Export to Excel"}
                  </button>
                </div>

                <div className="overflow-x-auto">
                  <table className="min-w-full text-left text-sm">
                    <thead className="bg-gray-50 text-gray-600">
                      <tr>
                        <th className="px-5 py-3 font-semibold">Metric</th>
                        <th className="px-5 py-3 font-semibold">Target</th>
                        <th className="px-5 py-3 font-semibold">Actual</th>
                        <th className="px-5 py-3 font-semibold">Variance</th>
                        <th className="px-5 py-3 font-semibold">Performance</th>
                        <th className="px-5 py-3 font-semibold">Status</th>
                      </tr>
                    </thead>
                    <tbody className="text-gray-700">
                      {detailRows.map((row) => {
                        const variance = row.actual - row.target;
                        const status = getKpiPerformanceStatus(
                          row.actual,
                          row.target,
                          isCurrentKpiHistoryPeriod,
                        );

                        return (
                          <tr
                            key={row.label}
                            className="border-t border-gray-100"
                          >
                            <td className="px-5 py-4 font-medium text-gray-800">
                              {row.label}
                            </td>
                            <td className="px-5 py-4">
                              {row.target > 0 ? row.prefix : ""}
                              {row.target > 0
                                ? row.target.toLocaleString()
                                : "-"}
                              {row.suffix || ""}
                            </td>
                            <td className="px-5 py-4">
                              {row.actual > 0 ? row.prefix : ""}
                              {row.actual > 0
                                ? row.actual.toLocaleString()
                                : "-"}
                              {row.suffix || ""}
                            </td>
                            <td
                              className={`px-5 py-4 font-semibold ${variance >= 0 ? "text-[#008060]" : "text-red-500"}`}
                            >
                              {variance > 0 ? "+" : "-"}
                              {variance > 0 && Math.abs(variance).toLocaleString()}
                            </td>
                            <td className="px-5 py-4">
                              <span className="inline-flex rounded-full bg-[#eaf7f3] px-2.5 py-1 text-xs font-bold text-[#008060]">
                                {row.performance ? `${row.performance}%` : "-"}
                              </span>
                            </td>
                            <td className="px-5 py-4">
                              <span
                                className={`inline-flex rounded-full px-2.5 py-1 text-xs font-bold ${
                                  status === "In Progress"
                                    ? "bg-amber-50 text-amber-700"
                                    : status === "Below Target"
                                      ? "bg-red-50 text-red-600"
                                      : "bg-[#eaf7f3] text-[#008060]"
                                }`}
                              >
                                {status}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* <div className="space-y-6">
              <div className="rounded-2xl border border-[#d7efe7] bg-[#f0faf7] p-5">
                <h3 className="mb-4 flex items-center gap-2 text-base font-bold text-gray-900">
                  <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#dfeee8] text-[#008060]">
                    💡
                  </span>
                  Key Takeaways
                </h3>

                <ul className="space-y-3 text-sm text-gray-700">
                  <li className="flex gap-2">
                    <span className="mt-1 h-2 w-2 rounded-full bg-[#008060]" />
                    Revenue was{" "}
                    {Math.max(0, 100 - (officer.revenuePerformance || 0))}%
                    below target.
                  </li>
                  <li className="flex gap-2">
                    <span className="mt-1 h-2 w-2 rounded-full bg-[#008060]" />
                    Registrations were{" "}
                    {Math.max(0, 100 - (officer.registrationPerformance || 0))}%
                    below target.
                  </li>
                  <li className="flex gap-2">
                    <span className="mt-1 h-2 w-2 rounded-full bg-[#008060]" />
                    Orders were{" "}
                    {Math.max(0, 100 - (officer.orderPerformance || 0))}% below
                    target.
                  </li>
                  <li className="flex gap-2">
                    <span className="mt-1 h-2 w-2 rounded-full bg-[#008060]" />
                    Overall performance is good, with an efficiency score of{" "}
                    {officer.efficiency || 0}%.
                  </li>
                </ul>
              </div>

              <div className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">
                <h3 className="mb-4 flex items-center gap-2 text-base font-bold text-gray-900">
                  <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#f3f4f6] text-gray-700">
                    ⏱
                  </span>
                  Performance Activity
                </h3>

                <div className="space-y-4">
                  {[
                    "1 Sep 2026, 09:00 AM — Monthly performance report generated",
                    "28 Aug 2026, 04:15 PM — Note added",
                    "25 Aug 2026, 11:20 AM — Targets reviewed with account officer",
                    "12 Aug 2026, 02:30 PM — Mid-month check-in completed",
                  ].map((item, index) => (
                    <div key={item} className="flex gap-3">
                      <div className="mt-0.5 flex h-4 w-4 items-center justify-center">
                        <span
                          className={`h-2.5 w-2.5 rounded-full ${index === 0 ? "bg-[#008060]" : "bg-gray-300"}`}
                        />
                      </div>
                      <p className="text-sm text-gray-600">{item}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div> */}
            </div>

            {/* <div className="px-6 pb-6">
            <div className="rounded-2xl border border-gray-100 bg-white shadow-sm">
              <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
                <div>
                  <h3 className="text-base font-bold text-gray-900">Notes</h3>
                  <p className="text-xs text-gray-400">
                    Keep track of important notes about this account
                    officer&apos;s performance.
                  </p>
                </div>
                <button className="rounded-lg border border-[#008060] bg-white px-3 py-1.5 text-sm font-medium text-[#008060] hover:bg-[#eaf7f3] transition-colors">
                  + Add Note
                </button>
              </div>

              <div className="space-y-3 p-5">
                {[
                  {
                    initials: "FA",
                    author: "Fikayomi Adebayo",
                    time: "2 Sep 2026, 10:24 AM",
                    note: "Good performance overall. Needs support with revenue targets for September.",
                  },
                  {
                    initials: "TO",
                    author: "Tobi Akinyemi",
                    time: "28 Aug 2026, 4:15 PM",
                    note: "Discussed strategies to improve order volume. AO is receptive.",
                  },
                  {
                    initials: "FA",
                    author: "Fikayomi Adebayo",
                    time: "15 Aug 2026, 11:03 AM",
                    note: "Mid-month review: on track with registrations. Revenue slightly behind.",
                  },
                ].map((note) => (
                  <div
                    key={note.note}
                    className="flex gap-3 rounded-xl border border-gray-100 bg-gray-50 p-4"
                  >
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#eaf7f3] text-xs font-bold text-[#008060]">
                      {note.initials}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between gap-3">
                        <div className="font-medium text-gray-800">
                          {note.author}
                        </div>
                        <div className="text-xs text-gray-400">{note.time}</div>
                      </div>
                      <p className="mt-2 text-sm text-gray-600">{note.note}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div> */}
          </section>
        )}
      </main>
    );
  }

  return (
    <main className="w-full">
      <div className="mb-8">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900">
          Account Management
        </h1>
        <p className="text-gray-500 text-xs sm:text-sm">
          Manage platform news and announcements
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <MetricCard
          label="Total Users"
          value={stats.totalUsers.toLocaleString()}
        />
        <MetricCard
          label="Assigned"
          value={stats.assignedUsers.toLocaleString()}
        />
        <MetricCard
          label="Unassigned"
          value={stats.unassignedUsers.toLocaleString()}
        />
        <MetricCard
          label="Active AOs"
          value={stats.totalActiveOfficers.toLocaleString()}
        />
      </div>

      <section className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
        <div className="flex items-center gap-8 border-b border-gray-100 mb-6 text-sm font-medium text-gray-500 overflow-x-auto">
          {[
            {
              id: "all_users",
              label: `All Users (${stats.totalUsers.toLocaleString()})`,
            },
            {
              id: "pending",
              label: `Pending Assignments(${stats.unassignedUsers.toLocaleString()})`,
            },
            { id: "officers", label: `Account Officers (${officers.length})` },
            { id: "audit_log", label: "Audit Log" },
            { id: "kpis", label: "KPIs" },
          ].map((tab) => {
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`pb-3 border-b-2 whitespace-nowrap transition-colors ${isActive ? "text-gray-800 border-[#008060] font-bold" : "border-transparent hover:text-gray-700"}`}
              >
                {tab.label}
              </button>
            );
          })}
        </div>

        {activeTab === "all_users" && (
          <>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
              <div>
                <h2 className="text-lg font-bold text-gray-800">
                  All Assigned Customers
                </h2>
                <p className="text-xs text-gray-400 mt-1">
                  View and reassign customers to different Account Officers
                </p>
              </div>
              <button
                onClick={() => setShowMigrateModal(true)}
                className="bg-[#008060] text-white px-6 py-2.5 rounded-lg text-sm font-medium hover:bg-[#006048] transition-colors"
              >
                Bulk Migrate
              </button>
            </div>

            <div className="mb-6">
              <input
                type="text"
                placeholder="Search by customer name, email..."
                value={allUsersSearch}
                onChange={(e) => setAllUsersSearch(e.target.value)}
                className="w-full bg-gray-50 border-none rounded-lg py-3 px-4 text-sm focus:ring-1 focus:ring-gray-200 outline-none placeholder-gray-400"
              />
            </div>

            {allUsersLoading ? (
              <div className="flex items-center justify-center py-16">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#008060]"></div>
                <span className="ml-3 text-sm text-gray-500">
                  Loading users...
                </span>
              </div>
            ) : allUsersError ? (
              <div className="text-center py-16">
                <p className="text-sm text-red-500 mb-2">{allUsersError}</p>
                <button
                  onClick={() =>
                    fetchAllUsers(allUsersPage, debouncedAllUsersSearch)
                  }
                  className="text-sm text-[#008060] underline hover:text-[#006048]"
                >
                  Retry
                </button>
              </div>
            ) : allUsers.length === 0 ? (
              <div className="text-center py-16">
                <p className="text-sm text-gray-400">No users found</p>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm text-left">
                    <thead className="bg-transparent text-gray-800 font-medium border-b border-gray-100">
                      <tr>
                        <th className="py-4 px-4 w-12">S/N</th>
                        <th className="py-4 px-4">Name</th>
                        <th className="py-4 px-4">E-Mail Address</th>
                        <th className="py-4 px-4">Phone Number</th>
                        <th className="py-4 px-4">Account Officer</th>
                        <th className="py-4 px-4 text-center">Order Count</th>
                        <th className="py-4 px-4">Assigned Date</th>
                        <th className="py-4 px-4">Action</th>
                      </tr>
                    </thead>
                    <tbody className="text-gray-500">
                      {allUsers.map((user, idx) => (
                        <tr
                          key={user.customerId || idx}
                          className="border-b border-gray-50 hover:bg-gray-50/50 transition-colors"
                        >
                          <td className="py-4 px-4 w-12 text-center text-gray-400 font-mono text-xs">
                            {(allUsersPage - 1) * 10 + idx + 1}
                          </td>
                          <td className="py-4 px-4 text-gray-800 font-medium">
                            {getCustomerDisplayName(user)}
                          </td>
                          <td className="py-4 px-4">{user.customerEmail}</td>
                          <td className="py-4 px-4">
                            {user.customerPhoneNumber}
                          </td>
                          <td className="py-4 px-4">
                            <span
                              className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${user.accountOfficer ? "bg-[#e6f5ee] text-[#008060]" : "bg-gray-100 text-gray-500"}`}
                            >
                              {user.accountOfficer || "Unassigned"}
                            </span>
                          </td>
                          <td className="py-4 px-4 text-center">
                            <button
                              onClick={() => {
                                setSelectedOrderHistoryUser(user);
                                setShowOrderHistoryModal(true);
                                fetchOrderHistory(
                                  user.customerId,
                                  user.customerEmail,
                                );
                              }}
                              className="inline-flex items-center justify-center font-semibold text-[#008060] hover:underline cursor-pointer transition-all outline-none font-mono text-sm"
                            >
                              {user.totalOrders || 0}
                            </button>
                          </td>
                          <td className="py-4 px-4">
                            {new Date(user.assignedDate).toLocaleDateString()}
                          </td>
                          <td className="py-4 px-4">
                            <div className="flex items-center gap-3">
                              <button
                                onClick={() => {
                                  setSelectedCustomerId(user.customerId);
                                  setShowReassignModal(true);
                                  setSelectedOfficerId(""); // Clear implementation
                                }}
                                className="text-[#008060] font-medium underline hover:text-[#006048]"
                              >
                                Re-assign
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mt-6 pt-4 border-t border-gray-100">
                  <p className="text-xs text-gray-400">
                    Showing {allUsers.length} of {allUsersTotal} users (page{" "}
                    {allUsersPage} of {allUsersTotalPages})
                  </p>
                  <PageNumberPagination
                    currentPage={allUsersPage}
                    totalPages={allUsersTotalPages}
                    onPageChange={(p) =>
                      fetchAllUsers(p, debouncedAllUsersSearch)
                    }
                    disabled={allUsersLoading}
                  />
                </div>
              </>
            )}
          </>
        )}

        {activeTab === "pending" && (
          <>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
              <div>
                <h2 className="text-lg font-bold text-gray-800">
                  Pending Customer Assignments
                </h2>
                <p className="text-xs text-gray-400 mt-1">
                  Customers waiting to be assigned to an Account Officer
                </p>
              </div>
              <button
                onClick={() => setShowBulkAssignModal(true)}
                className="bg-[#008060] text-white px-6 py-2.5 rounded-lg text-sm font-medium hover:bg-[#006048] transition-colors"
              >
                Bulk Assignment
              </button>
            </div>

            <div className="mb-6">
              <input
                type="text"
                placeholder="Search by customer name, email or phone..."
                value={pendingSearch}
                onChange={(e) => setPendingSearch(e.target.value)}
                className="w-full bg-gray-50 border-none rounded-lg py-3 px-4 text-sm focus:ring-1 focus:ring-gray-200 outline-none placeholder-gray-400"
              />
            </div>

            {pendingCustomersLoading ? (
              <div className="flex items-center justify-center py-16">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#008060]"></div>
                <span className="ml-3 text-sm text-gray-500">
                  Loading pending customers...
                </span>
              </div>
            ) : pendingCustomersError ? (
              <div className="text-center py-16">
                <p className="text-sm text-red-500 mb-2">
                  {pendingCustomersError}
                </p>
                <button
                  onClick={() => window.location.reload()}
                  className="text-sm text-[#008060] underline hover:text-[#006048]"
                >
                  Retry
                </button>
              </div>
            ) : pendingCustomers.length === 0 ? (
              <div className="text-center py-16">
                <p className="text-sm text-gray-400">
                  No pending assignments found
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm text-left">
                  <thead className="bg-transparent text-gray-800 font-medium border-b border-gray-100">
                    <tr>
                      <th className="py-4 px-4 w-12">
                        <button
                          onClick={toggleAllPendingUsers}
                          className="focus:outline-none flex items-center"
                        >
                          {selectedPendingUsers.length ===
                            pendingCustomers.length &&
                          pendingCustomers.length > 0 ? (
                            <img
                              src="/assets/material-symbols-light_select-check-box.svg"
                              alt="checked"
                              className="w-6 h-6"
                            />
                          ) : (
                            <div className="w-6 h-6 border-2 border-gray-300 rounded flex items-center justify-center hover:border-gray-400 transition-colors"></div>
                          )}
                        </button>
                      </th>
                      <th className="py-4 px-4">Name</th>
                      <th className="py-4 px-4">Email</th>
                      <th className="py-4 px-4">Phone</th>
                      <th className="py-4 px-4 text-center">Orders</th>
                      <th className="py-4 px-4">Registration Date</th>
                      <th className="py-4 px-4">Action</th>
                    </tr>
                  </thead>
                  <tbody className="text-gray-500">
                    {pendingCustomers.map((user: any, idx: number) => (
                      <tr
                        key={user.customerId || idx}
                        className="border-b border-gray-50 hover:bg-gray-50/50 transition-colors"
                      >
                        <td className="py-4 px-4">
                          <button
                            onClick={() => togglePendingUser(user.customerId)}
                            className="focus:outline-none flex items-center"
                          >
                            {selectedPendingUsers.includes(user.customerId) ? (
                              <img
                                src="/assets/material-symbols-light_select-check-box.svg"
                                alt="checked"
                                className="w-6 h-6"
                              />
                            ) : (
                              <div className="w-6 h-6 border-2 border-gray-300 rounded flex items-center justify-center hover:border-gray-400 transition-colors"></div>
                            )}
                          </button>
                        </td>
                        <td className="py-4 px-4 text-gray-800 font-medium">
                          {getCustomerDisplayName(user)}
                        </td>
                        <td className="py-4 px-4">{user.customerEmail}</td>
                        <td className="py-4 px-4">{user.phoneNumber || "-"}</td>
                        <td className="py-4 px-4 text-center">
                          <button
                            onClick={() => {
                              setSelectedOrderHistoryUser({
                                customerName: user.customerName,
                                totalOrders: user.totalOrders,
                              });
                              setShowOrderHistoryModal(true);
                              fetchOrderHistory(
                                user.customerId,
                                user.customerEmail,
                              );
                            }}
                            className="inline-flex items-center justify-center font-semibold text-[#008060] hover:underline cursor-pointer transition-all outline-none font-mono text-sm"
                          >
                            {user.totalOrders || 0}
                          </button>
                        </td>
                        <td className="py-4 px-4">
                          {new Date(user.registrationDate).toLocaleDateString()}
                        </td>
                        <td className="py-4 px-4">
                          <button
                            onClick={() => {
                              setSelectedCustomerId(user.customerId);
                              setShowAssignModal(true);
                            }}
                            disabled={
                              selectedPendingUsers.length > 0 &&
                              !selectedPendingUsers.includes(user.customerId)
                            } // Optional: disable individual action if bulk selection is active? Or just allow it.
                            className="text-[#008060] font-medium underline hover:text-[#006048]"
                          >
                            Assign
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mt-6 pt-4 border-t border-gray-100">
              <p className="text-xs text-gray-400">
                Showing {pendingCustomers.length} of {pendingTotal} pending
                assignments (page {pendingPage} of {pendingTotalPages})
              </p>
              <PageNumberPagination
                currentPage={pendingPage}
                totalPages={pendingTotalPages}
                onPageChange={(p) =>
                  fetchPendingCustomers(p, debouncedPendingSearch)
                }
                disabled={pendingCustomersLoading}
              />
            </div>
          </>
        )}

        {activeTab === "officers" && (
          <>
            <div className="mb-6">
              <h2 className="text-lg font-bold text-gray-800">
                Account Officers Management
              </h2>
              <p className="text-xs text-gray-400 mt-1">
                Manage Account Officer status and view their performance
              </p>
            </div>

            <div className="mb-6">
              <input
                type="text"
                placeholder="Search by officer name, email or phone..."
                value={officersSearch}
                onChange={(e) => setOfficersSearch(e.target.value)}
                className="w-full bg-gray-50 border-none rounded-lg py-3 px-4 text-sm focus:ring-1 focus:ring-gray-200 outline-none placeholder-gray-400"
              />
            </div>

            {officersLoading ? (
              <div className="flex items-center justify-center py-16">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#008060]"></div>
                <span className="ml-3 text-sm text-gray-500">
                  Loading account officers...
                </span>
              </div>
            ) : officersError ? (
              <div className="text-center py-16">
                <p className="text-sm text-red-500 mb-2">{officersError}</p>
                <button
                  onClick={() => window.location.reload()}
                  className="text-sm text-[#008060] underline hover:text-[#006048]"
                >
                  Retry
                </button>
              </div>
            ) : officers.length === 0 ? (
              <div className="text-center py-16">
                <p className="text-sm text-gray-400">
                  No account officers found
                </p>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm text-left">
                    <thead className="bg-transparent text-gray-800 font-medium border-b border-gray-100">
                      <tr>
                        <th className="py-4 px-4">S/N</th>
                        <th className="py-4 px-4">Name</th>
                        <th className="py-4 px-4">E-Mail Address</th>
                        <th className="py-4 px-4">Phone Number</th>
                        <th className="py-4 px-4 text-center">Customers</th>
                        <th className="py-4 px-4">Total Revenue</th>
                        <th className="py-4 px-4 text-center">Referral Code</th>
                        <th className="py-4 px-4">Status</th>
                        <th className="py-4 px-4 text-center">Action</th>
                      </tr>
                    </thead>
                    <tbody className="text-gray-500">
                      {paginatedOfficersList.map(
                        (officer: any, idx: number) => (
                          <tr
                            key={officer.staffId || idx}
                            className="border-b border-gray-50 hover:bg-gray-50/50 transition-colors"
                          >
                            <td className="py-4 px-4">
                              {(officersPage - 1) * 10 + idx + 1}
                            </td>
                            <td className="py-4 px-4 text-gray-800 font-medium">
                              {officer.name}
                            </td>
                            <td className="py-4 px-4">
                              <span
                                className="cursor-pointer hover:text-[#008060] transition-colors"
                                onClick={() =>
                                  copyToClipboard(
                                    officer.officialEmail || officer.email,
                                    "Email",
                                  )
                                }
                                title="Copy Email"
                              >
                                {officer.officialEmail || officer.email}
                              </span>
                            </td>
                            <td className="py-4 px-4">
                              <div className="flex items-center gap-2">
                                <span
                                  className="cursor-pointer hover:text-[#008060] transition-colors"
                                  onClick={() =>
                                    copyToClipboard(
                                      officer.phoneNumber,
                                      "Phone Number",
                                    )
                                  }
                                  title="Copy Phone"
                                >
                                  {officer.phoneNumber}
                                </span>
                                {officer.phoneNumber && (
                                  <a
                                    href={`https://wa.me/${officer.phoneNumber?.replace(/^0/, "234").replace(/[^0-9]/g, "")}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    title="Chat on WhatsApp"
                                    className="text-[#25D366] hover:text-[#128C7E] transition-colors"
                                  >
                                    <FaWhatsapp size={16} />
                                  </a>
                                )}
                              </div>
                            </td>
                            <td className="py-4 px-4 text-center">
                              {officer.assignedCustomersCount}
                            </td>
                            <td className="py-4 px-4">
                              ₦{(officer.totalRevenue || 0).toLocaleString()}
                            </td>
                            <td className="py-4 px-4 text-center">
                              <span
                                className="bg-gray-100 text-gray-600 px-3 py-1 rounded text-xs font-mono cursor-pointer hover:bg-gray-200 hover:text-[#008060] transition-colors"
                                onClick={() =>
                                  copyToClipboard(
                                    officer.referralCode,
                                    "Referral Code",
                                  )
                                }
                                title="Copy Code"
                              >
                                {officer.referralCode}
                              </span>
                            </td>
                            <td className="py-4 px-4">
                              <span
                                className={`${officer.status === "active" ? "text-[#008060]" : "text-gray-400"} font-medium capitalize`}
                              >
                                {officer.status}
                              </span>
                            </td>
                            <td className="py-4 px-4 text-center">
                              <button
                                onClick={() =>
                                  handleOpenOfficerKpiHistory(officer)
                                }
                                className="text-[#008060] hover:text-[#006048] font-medium text-xs underline transition-colors"
                              >
                                View Details
                              </button>
                            </td>
                          </tr>
                        ),
                      )}
                    </tbody>
                  </table>
                </div>

                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mt-6 pt-4 border-t border-gray-100">
                  <p className="text-xs text-gray-400">
                    Showing {paginatedOfficersList.length} of{" "}
                    {computedOfficersTotal} account officers (page{" "}
                    {officersPage} of {computedOfficersTotalPages})
                  </p>
                  <PageNumberPagination
                    currentPage={officersPage}
                    totalPages={computedOfficersTotalPages}
                    onPageChange={setOfficersPage}
                    disabled={officersLoading}
                  />
                </div>
              </>
            )}
          </>
        )}

        {activeTab === "audit_log" && (
          <>
            <div className="mb-8">
              <h2 className="text-lg font-bold text-gray-800">Audit Logs</h2>
              <p className="text-xs text-gray-400 mt-1">
                Complete history of all customer assignments and changes
              </p>
            </div>

            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
              <div className="flex items-center gap-1 text-sm border-b border-gray-100 w-fit">
                {["all", "today", "this_week", "this_month", "last_month"].map(
                  (filter) => {
                    const labels: Record<string, string> = {
                      all: "All",
                      today: "Today",
                      this_week: "This Week",
                      this_month: "This Month",
                      last_month: "Last Month",
                    };
                    return (
                      <button
                        key={filter}
                        onClick={() => setAuditDateFilter(filter)}
                        className={`pb-2 px-3 border-b-2 transition-colors ${
                          auditDateFilter === filter
                            ? "border-[#008060] font-medium text-gray-800"
                            : "border-transparent text-gray-500 hover:text-gray-700"
                        }`}
                      >
                        {labels[filter]}
                      </button>
                    );
                  },
                )}
              </div>
              <select
                value={auditActionFilter}
                onChange={(e) => setAuditActionFilter(e.target.value)}
                className="bg-gray-50 border border-gray-200 rounded-lg py-2 px-3 text-sm text-gray-600 outline-none focus:ring-1 focus:ring-[#008060] cursor-pointer"
              >
                <option value="all">All Action Types</option>
                <option value="MANUAL_ASSIGNMENT">Manual Assignment</option>
                <option value="CUSTOMER_UNASSIGNED">Customer Unassigned</option>
                <option value="OFFICER_CREATED">Officer Created</option>
                <option value="OFFICER_UPDATED">Officer Updated</option>
                <option value="OFFICER_DEACTIVATED">Officer Deactivated</option>
              </select>
            </div>

            <div className="mb-6">
              <input
                type="text"
                placeholder="Search audit logs by name, action or performed by..."
                value={auditSearch}
                onChange={(e) => setAuditSearch(e.target.value)}
                className="w-full bg-gray-50 border border-gray-100 rounded-lg py-3 px-4 text-sm focus:ring-1 focus:ring-[#008060] outline-none placeholder-gray-400"
              />
            </div>

            {auditLogsLoading ? (
              <div className="flex items-center justify-center py-16">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#008060]"></div>
                <span className="ml-3 text-sm text-gray-500">
                  Loading audit logs...
                </span>
              </div>
            ) : auditLogsError ? (
              <div className="text-center py-16">
                <p className="text-sm text-red-500 mb-2">{auditLogsError}</p>
                <button
                  onClick={() => window.location.reload()}
                  className="text-sm text-[#008060] underline hover:text-[#006048]"
                >
                  Retry
                </button>
              </div>
            ) : filteredAuditLogs.length === 0 ? (
              <div className="text-center py-16">
                <p className="text-sm text-gray-400">
                  {auditDateFilter !== "all" || auditActionFilter !== "all"
                    ? "No audit logs match the current filters"
                    : "No audit logs found"}
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {filteredAuditLogs.map((log: any, idx: number) => {
                  const actionLabel = (log.actionType || "ACTION")
                    .replace(/_/g, " ")
                    .toLowerCase()
                    .replace(/\b\w/g, (c: string) => c.toUpperCase());
                  const isDestructive = [
                    "CUSTOMER_UNASSIGNED",
                    "OFFICER_DEACTIVATED",
                    "OFFICER_DELETED",
                  ].includes(log.actionType);
                  return (
                    <div
                      key={log.id || idx}
                      className="bg-white border border-gray-100 rounded-xl p-6 shadow-sm"
                    >
                      <h3
                        className={`text-sm font-semibold mb-2 ${isDestructive ? "text-[#ff3b30]" : "text-[#008060]"}`}
                      >
                        {actionLabel}
                      </h3>
                      <div className="space-y-1 text-sm text-gray-600">
                        {log.customerName && (
                          <p>
                            <span className="text-gray-400">Customer:</span>{" "}
                            <span className="font-medium text-gray-800">
                              {log.customerName}
                            </span>
                          </p>
                        )}
                        {log.accountOfficerName && (
                          <p>
                            <span className="text-gray-400">
                              Account Officer:
                            </span>{" "}
                            <span className="font-medium text-gray-800">
                              {log.accountOfficerName}
                            </span>
                          </p>
                        )}
                        {log.previousOfficerName && (
                          <p>
                            <span className="text-gray-400">
                              Previous Officer:
                            </span>{" "}
                            <span className="font-medium text-gray-800">
                              {log.previousOfficerName}
                            </span>
                          </p>
                        )}
                        {log.metadata?.oldStatus && log.metadata?.newStatus && (
                          <p>
                            <span className="text-gray-400">
                              Status Change:
                            </span>{" "}
                            <span className="font-medium text-gray-800 capitalize">
                              {log.metadata.oldStatus} →{" "}
                              {log.metadata.newStatus}
                            </span>
                          </p>
                        )}
                        <p className="text-xs text-[#008060] mt-2">
                          By:{" "}
                          <span className="font-medium">{log.performedBy}</span>
                          {log.adminRole && (
                            <span className="text-gray-400">
                              {" "}
                              ({log.adminRole})
                            </span>
                          )}
                          {" • "}
                          <span className="text-gray-400">
                            {new Date(log.createdAt).toLocaleString()}
                          </span>
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}

        {activeTab === "kpis" && (
          <div className="flex flex-col gap-5 w-full">
            <div className="flex items-center gap-8 border-b border-gray-100 mb-6 text-sm font-medium text-gray-500 overflow-x-auto">
              {[
                {
                  id: "monthly_kpis",
                  label: "Set Monthly KPIs",
                },
                {
                  id: "history",
                  label: "KPI History",
                },
              ].map((tab) => {
                const isActive = activeKPI === tab.id;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveKPI(tab.id as typeof activeKPI)}
                    className={`pb-3 border-b-2 whitespace-nowrap transition-colors ${isActive ? "text-gray-800 border-[#008060] font-bold" : "border-transparent hover:text-gray-700"}`}
                  >
                    {tab.label}
                  </button>
                );
              })}
            </div>

            {activeKPI === "monthly_kpis" && (
              <div className="animate-in fade-in duration-300">
                <div className="mb-8">
                  <h2 className="text-lg font-bold text-gray-800">
                    Monthly KPIs
                  </h2>
                  <p className="text-xs text-gray-400 mt-1">
                    Set and manage Key Performance Indicators for the platform
                  </p>
                </div>

                <div className="bg-gray-50 rounded-xl p-6 border border-gray-100 mb-8">
                  <h3 className="text-sm font-semibold text-gray-700 mb-4">
                    Select Period
                  </h3>
                  <div className="flex flex-col sm:flex-row gap-4 max-w-md">
                    <div className="flex-1">
                      <CustomSelect
                        value={kpiMonth}
                        onChange={setKpiMonth}
                        options={availableMonths.map((m) => ({
                          label: m,
                          value: m,
                        }))}
                      />
                    </div>
                    <div className="flex-1">
                      <CustomSelect
                        value={kpiYear}
                        onChange={(val) => {
                          setKpiYear(val);
                          if (
                            val === currentYear &&
                            months.indexOf(kpiMonth) < today.getMonth()
                          ) {
                            setKpiMonth(currentMonth);
                          }
                        }}
                        options={availableYears.map((y) => ({
                          label: y,
                          value: y,
                        }))}
                      />
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-xl p-6 border border-gray-100 mb-8 mt-4">
                  <h3 className="text-sm font-semibold text-gray-700 mb-4">
                    Select Account Officer
                  </h3>
                  <div className="max-w-md">
                    <CustomSelect
                      value={kpiOfficerId}
                      onChange={setKpiOfficerId}
                      placeholder="Select an Account Officer..."
                      options={[
                        { label: "All Account Officers", value: "all" },
                        ...officers
                          .filter((o) => o.status === "active")
                          .map((officer: any) => ({
                            label: officer.name,
                            value: officer.staffId,
                          })),
                      ]}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                  <div
                    className={`bg-white border rounded-xl p-5 shadow-sm transition-colors group ${isRevenueActive ? "border-gray-100 hover:border-[#008060]/30" : "border-gray-50 opacity-70"}`}
                  >
                    <div className="flex items-center justify-between mb-4">
                      <label className="text-sm font-medium text-gray-700">
                        Expected Revenue (₦)
                      </label>
                      <button
                        onClick={() => setIsRevenueActive(!isRevenueActive)}
                        className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${isRevenueActive ? "bg-[#008060]" : "bg-gray-200"}`}
                      >
                        <span
                          className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${isRevenueActive ? "translate-x-4" : "translate-x-0"}`}
                        />
                      </button>
                    </div>
                    <div className="relative">
                      <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 font-medium">
                        ₦
                      </span>
                      <input
                        type="number"
                        value={expectedRevenue}
                        onChange={(e) => setExpectedRevenue(e.target.value)}
                        placeholder="0.00"
                        disabled={!isRevenueActive}
                        className="w-full bg-gray-50 border border-gray-100 rounded-lg py-3 pl-10 pr-4 text-sm focus:bg-white focus:ring-2 focus:ring-[#008060]/20 focus:border-[#008060] outline-none transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                      />
                    </div>
                    <p className="text-[11px] text-gray-400 mt-2">
                      Target revenue for {kpiMonth}
                    </p>
                  </div>

                  <div
                    className={`bg-white border rounded-xl p-5 shadow-sm transition-colors group ${isNewCustomersActive ? "border-gray-100 hover:border-[#008060]/30" : "border-gray-50 opacity-70"}`}
                  >
                    <div className="flex items-center justify-between mb-4">
                      <label className="text-sm font-medium text-gray-700">
                        Expected User Registrations
                      </label>
                      <button
                        onClick={() =>
                          setIsNewCustomersActive(!isNewCustomersActive)
                        }
                        className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${isNewCustomersActive ? "bg-[#008060]" : "bg-gray-200"}`}
                      >
                        <span
                          className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${isNewCustomersActive ? "translate-x-4" : "translate-x-0"}`}
                        />
                      </button>
                    </div>
                    <input
                      type="number"
                      value={expectedNewCustomers}
                      onChange={(e) => setExpectedNewCustomers(e.target.value)}
                      placeholder="e.g. 50"
                      disabled={!isNewCustomersActive}
                      className="w-full bg-gray-50 border border-gray-100 rounded-lg py-3 px-4 text-sm focus:bg-white focus:ring-2 focus:ring-[#008060]/20 focus:border-[#008060] outline-none transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                    />
                    <p className="text-[11px] text-gray-400 mt-2">
                      Via referral code
                    </p>
                  </div>

                  <div
                    className={`bg-white border rounded-xl p-5 shadow-sm transition-colors group ${isTotalOrdersActive ? "border-gray-100 hover:border-[#008060]/30" : "border-gray-50 opacity-70"}`}
                  >
                    <div className="flex items-center justify-between mb-4">
                      <label className="text-sm font-medium text-gray-700">
                        Total Number of Expected Orders
                      </label>
                      <button
                        onClick={() =>
                          setIsTotalOrdersActive(!isTotalOrdersActive)
                        }
                        className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${isTotalOrdersActive ? "bg-[#008060]" : "bg-gray-200"}`}
                      >
                        <span
                          className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${isTotalOrdersActive ? "translate-x-4" : "translate-x-0"}`}
                        />
                      </button>
                    </div>
                    <input
                      type="number"
                      value={expectedTotalOrders}
                      onChange={(e) => setExpectedTotalOrders(e.target.value)}
                      placeholder="e.g. 500"
                      disabled={!isTotalOrdersActive}
                      className="w-full bg-gray-50 border border-gray-100 rounded-lg py-3 px-4 text-sm focus:bg-white focus:ring-2 focus:ring-[#008060]/20 focus:border-[#008060] outline-none transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                    />
                    <p className="text-[11px] text-gray-400 mt-2">
                      Target total order volume for {kpiMonth}
                    </p>
                  </div>

                  {/* <div className={`bg-white border rounded-xl p-5 shadow-sm transition-colors group ${isConversionRateActive ? 'border-gray-100 hover:border-[#008060]/30' : 'border-gray-50 opacity-70'}`}>
                                      <div className="flex items-center justify-between mb-4">
                                          <label className="text-sm font-medium text-gray-700">Expected Conversion Rate (%)</label>
                                          <button
                                              onClick={() => setIsConversionRateActive(!isConversionRateActive)}
                                              className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${isConversionRateActive ? 'bg-[#008060]' : 'bg-gray-200'}`}
                                          >
                                              <span className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${isConversionRateActive ? 'translate-x-4' : 'translate-x-0'}`} />
                                          </button>
                                      </div>
                                      <div className="relative">
                                          <input
                                              type="number"
                                              value={conversionRate}
                                              onChange={(e) => setConversionRate(e.target.value)}
                                              placeholder="e.g. 5.5"
                                              disabled={!isConversionRateActive}
                                              className="w-full bg-gray-50 border border-gray-100 rounded-lg py-3 px-4 text-sm focus:bg-white focus:ring-2 focus:ring-[#008060]/20 focus:border-[#008060] outline-none transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                                          />
                                          <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 font-medium">%</span>
                                      </div>
                                      <p className="text-[11px] text-gray-400 mt-2">Target conversion rate for {kpiMonth}</p>
                                  </div> */}
                </div>

                <div className="flex justify-end pt-4 border-t border-gray-100">
                  <button
                    onClick={handleSaveKPIs}
                    disabled={kpiSaving}
                    className="bg-[#008060] text-white px-8 py-3 rounded-lg text-sm font-semibold hover:bg-[#006048] hover:shadow-md hover:-translate-y-0.5 transition-all outline-none focus:ring-4 focus:ring-[#008060]/20 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {kpiSaving
                      ? isEditMode
                        ? "Updating..."
                        : "Saving..."
                      : isEditMode
                        ? "Update KPIs"
                        : "Save KPIs"}
                  </button>
                </div>

                {/* <div className="mt-12">
                                  <h3 className="text-lg font-bold text-gray-800 mb-4">Set KPIs Overview</h3>
                                  <div className="bg-white border border-gray-100 rounded-xl overflow-hidden shadow-sm">
                                      <div className="overflow-x-auto">
                                          <table className="w-full text-sm text-left">
                                              <thead className="bg-gray-50 text-gray-600 font-medium border-b border-gray-100">
                                                  <tr>
                                                      <th className="py-4 px-6">Account Officer</th>
                                                      <th className="py-4 px-6">Period</th>
                                                      <th className="py-4 px-6">Target Revenue</th>
                                                      <th className="py-4 px-6 text-center">New Signups</th>
                                                      <th className="py-4 px-6 text-center">Expected Orders</th>
                                                      <th className="py-4 px-6 text-center">Conv. Rate</th>
                                                      <th className="py-4 px-6 text-right">Action</th>
                                                  </tr>
                                              </thead>
                                              <tbody className="text-gray-500">
                                                  {kpisLoading ? (
                                                      <tr>
                                                          <td colSpan={6} className="py-8 text-center">
                                                              <div className="flex justify-center items-center">
                                                                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#008060]"></div>
                                                              </div>
                                                          </td>
                                                      </tr>
                                                  ) : kpiList.map((kpi, idx) => (
                                                      <tr key={idx} className="border-b border-gray-50 hover:bg-gray-50/50 transition-colors">
                                                          <td className="py-4 px-6 text-gray-800 font-medium">{kpi.accountOfficerDetails?.name || kpi.officerName || "Unknown"}</td>
                                                          <td className="py-4 px-6">
                                                              <span className="inline-block px-2.5 py-1 bg-gray-100 text-gray-700 rounded-md text-xs font-mono">
                                                                  {kpi.period}
                                                              </span>
                                                          </td>
                                                          <td className="py-4 px-6 font-semibold text-[#008060]">
                                                              {kpi.targetRevenue > 0 ? `₦${kpi.targetRevenue.toLocaleString()}` : "—"}
                                                          </td>
                                                          <td className="py-4 px-6 text-center">
                                                              {kpi.expectedUserRegistrations > 0 ? kpi.expectedUserRegistrations : "—"}
                                                          </td>
                                                          <td className="py-4 px-6 text-center">
                                                              {kpi.totalExpectedOrders > 0 ? kpi.totalExpectedOrders : "—"}
                                                          </td>
                                                          <td className="py-4 px-6 text-center">
                                                              {kpi.targetConversionRate > 0 ? `${kpi.targetConversionRate}%` : "—"}
                                                          </td>
                                                          <td className="py-4 px-6 text-right">
                                                              <div className="flex justify-end items-center gap-3">
                                                                  <button
                                                                      onClick={() => {
                                                                          window.scrollTo({ top: 0, behavior: "smooth" });
                                                                          const [yr, mo] = kpi.period.split("-");
                                                                          setKpiYear(yr);
                                                                          setKpiMonth(months[parseInt(mo) - 1]);
                                                                          setKpiOfficerId(kpi.accountOfficerId || kpi.officerId);
                                                                      }}
                                                                      className="p-2 text-[#008060] hover:bg-[#e6f5ee] rounded-lg transition-colors"
                                                                      title="Edit KPI"
                                                                  >
                                                                      <FiEdit size={18} />
                                                                  </button>
                                                                  <button
                                                                      onClick={() => handleDeleteKPI(kpi.period, kpi.accountOfficerId || kpi.officerId)}
                                                                      className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                                                                      title="Delete KPI"
                                                                  >
                                                                      <FiTrash2 size={18} />
                                                                  </button>
                                                              </div>
                                                          </td>
                                                      </tr>
                                                  ))}
                                                  {!kpisLoading && kpiList.length === 0 && (
                                                      <tr>
                                                          <td colSpan={7} className="py-8 text-center text-gray-400">
                                                              No KPIs set yet
                                                          </td>
                                                      </tr>
                                                  )}
                                              </tbody>
                                          </table>
                                      </div>
                                  </div>
                              </div> */}
              </div>
            )}

            {activeKPI === "history" && (
              <div className="animate-in fade-in duration-300">
                <div className="space-y-6">
                  <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                    <div>
                      <h2 className="text-lg font-bold text-gray-800">
                        KPI History
                      </h2>
                      <p className="text-xs text-gray-400 mt-1">
                        View and compare KPI performance for all account
                        officers.
                      </p>
                    </div>

                    <div className="flex flex-col sm:flex-row gap-3">
                      <div className="w-full sm:w-40">
                        <CustomSelect
                          value={kpiHistoryYear}
                          onChange={handleKpiHistoryYearChange}
                          options={historyYears.map((year) => ({
                            label: year,
                            value: year,
                          }))}
                        />
                      </div>
                      <div className="w-full sm:w-40">
                        <CustomSelect
                          value={kpiHistoryMonth}
                          onChange={handleKpiHistoryMonthChange}
                          options={historyMonthOptions}
                        />
                      </div>
                      <div className="w-full sm:w-48">
                        <CustomSelect
                          value={kpiHistoryOfficerId}
                          onChange={handleKpiHistoryOfficerChange}
                          options={[
                            { label: "All Account Officers", value: "all" },
                            ...officers
                              .filter((officer) => officer.status === "active")
                              .map((officer: any) => ({
                                label: officer.name,
                                value: officer.staffId,
                              })),
                          ]}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
                    <div className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">
                      <div className="mb-3 flex items-center justify-between text-sm text-gray-500">
                        <span>Total Revenue (Actual)</span>
                        <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#eaf7f3] text-[#008060] font-bold">
                          ₦
                        </span>
                      </div>
                      <div className="text-3xl font-bold text-gray-900">
                        ₦
                        {(
                          kpiHistoryData?.summary.revenue.totalActual ?? 0
                        ).toLocaleString()}
                      </div>
                      <div className="mt-2 text-xs text-[#008060]">
                        {kpiHistoryData?.summary.revenue.achievementPercent ??
                          0}
                        % achieved of ₦
                        {(
                          kpiHistoryData?.summary.revenue.combinedTarget ?? 0
                        ).toLocaleString()}{" "}
                        target
                      </div>
                    </div>

                    <div className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">
                      <div className="mb-3 flex items-center justify-between text-sm text-gray-500">
                        <span>Total Registrations (Actual)</span>
                        <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#f3e8ff] text-[#8b5cf6] font-bold">
                          ◉
                        </span>
                      </div>
                      <div className="text-3xl font-bold text-gray-900">
                        {(
                          kpiHistoryData?.summary.registrations.totalActual ?? 0
                        ).toLocaleString()}
                      </div>
                      <div className="mt-2 text-xs text-[#8b5cf6]">
                        {kpiHistoryData?.summary.registrations
                          .achievementPercent ?? 0}
                        % achieved of{" "}
                        {(
                          kpiHistoryData?.summary.registrations
                            .combinedTarget ?? 0
                        ).toLocaleString()}{" "}
                        target
                      </div>
                    </div>

                    <div className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">
                      <div className="mb-3 flex items-center justify-between text-sm text-gray-500">
                        <span>Total Orders (Actual)</span>
                        <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#fff7ed] text-[#f59e0b] font-bold">
                          ▣
                        </span>
                      </div>
                      <div className="text-3xl font-bold text-gray-900">
                        {(
                          kpiHistoryData?.summary.orders.totalActual ?? 0
                        ).toLocaleString()}
                      </div>
                      <div className="mt-2 text-xs text-[#f59e0b]">
                        {kpiHistoryData?.summary.orders.achievementPercent ?? 0}
                        % achieved of{" "}
                        {(
                          kpiHistoryData?.summary.orders.combinedTarget ?? 0
                        ).toLocaleString()}{" "}
                        target
                      </div>
                    </div>

                    <div className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">
                      <div className="mb-3 flex items-center justify-between text-sm text-gray-500">
                        <span>Average Performance</span>
                        <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#e6fffb] text-[#14b8a6] font-bold">
                          ↗
                        </span>
                      </div>
                      <div className="text-3xl font-bold text-gray-900">
                        {Math.round(
                          kpiHistoryData?.summary.averagePerformance ?? 0,
                        )}
                        %
                      </div>
                      <div className="mt-2 text-xs text-[#14b8a6]">
                        across all account officers
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col gap-4 rounded-2xl border border-gray-100 bg-white shadow-sm overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="min-w-full text-left text-sm">
                        <thead className="bg-gray-50 text-gray-600">
                          <tr>
                            <th className="px-5 py-4 font-semibold">#</th>
                            <th className="px-5 py-4 font-semibold">
                              Account Officer
                            </th>
                            <th className="px-5 py-4 font-semibold">
                              Revenue (₦)
                            </th>
                            <th className="px-5 py-4 font-semibold">
                              Registrations
                            </th>
                            <th className="px-5 py-4 font-semibold">Orders</th>
                            <th className="px-5 py-4 font-semibold">
                              Overall Performance
                            </th>
                            <th className="px-5 py-4 font-semibold text-right">
                              Actions
                            </th>
                          </tr>
                        </thead>

                        <tbody className="text-gray-700">
                          {kpiHistoryLoading ? (
                            <tr>
                              <td
                                colSpan={7}
                                className="p-5 text-center text-sm text-gray-500"
                                role="status"
                                aria-live="polite"
                              >
                                <div className="flex flex-row items-center justify-center gap-2">
                                  <span className="h-5 w-5 animate-spin rounded-full border-2 border-gray-200 border-t-[#008060]" />
                                  <span> Loading KPI history...</span>
                                </div>
                              </td>
                            </tr>
                          ) : historyRows.length === 0 ? (
                            <tr>
                              <td
                                colSpan={7}
                                className="px-5 py-12 text-center text-sm text-gray-500"
                              >
                                No available data
                              </td>
                            </tr>
                          ) : (
                            historyRows.map((row, idx) => (
                              <tr
                                key={row.staffId || idx}
                                className="border-t border-gray-100"
                              >
                                <td className="px-5 py-4 text-gray-500">
                                  {(kpiHistoryPage - 1) * 10 + idx + 1}
                                </td>
                                <td className="px-5 py-4">
                                  <div className="flex items-center gap-3">
                                    <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[#edf4f1] text-xs font-bold text-[#008060]">
                                      {row.name?.charAt(0)?.toUpperCase() ||
                                        "A"}
                                    </div>
                                    <div className="font-medium text-gray-800">
                                      {row.name}
                                    </div>
                                  </div>
                                </td>
                                <td className="px-5 py-4">
                                  <div className="font-medium text-gray-800">
                                    ₦{row.actualRevenue.toLocaleString()}
                                  </div>
                                  <div className="text-xs text-gray-500">
                                    Target: ₦
                                    {row.revenueTarget.toLocaleString()}
                                  </div>
                                </td>
                                <td className="px-5 py-4">
                                  <div className="font-medium text-gray-800">
                                    {row.actualRegistrations}
                                  </div>
                                  <div className="text-xs text-gray-500">
                                    Target: {row.registrationTarget}
                                  </div>
                                </td>
                                <td className="px-5 py-4">
                                  <div className="font-medium text-gray-800">
                                    {row.actualOrders}
                                  </div>
                                  <div className="text-xs text-gray-500">
                                    Target: {row.orderTarget}
                                  </div>
                                </td>
                                <td className="px-5 py-4">
                                  <div className="flex items-center gap-3">
                                    <div className="h-2.5 w-20 overflow-hidden rounded-full bg-gray-100">
                                      <div
                                        className="h-full rounded-full bg-[#008060]"
                                        style={{
                                          width: `${Math.min(100, row.overallPerformance)}%`,
                                        }}
                                      />
                                    </div>
                                    <span className="text-sm font-semibold text-gray-700">
                                      {row.overallPerformance}%
                                    </span>
                                  </div>
                                </td>
                                <td className="px-5 py-4 text-right">
                                  <button
                                    type="button"
                                    onClick={() =>
                                      handleOpenOfficerKpiHistory(row)
                                    }
                                    className="inline-flex items-center gap-2 rounded-lg border border-gray-200 px-3 py-1.5 text-sm font-medium text-gray-700 hover:border-[#008060] hover:text-[#008060] transition-colors"
                                  >
                                    View Details
                                  </button>
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>

                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 border-t border-gray-100 px-5 py-4">
                      <p className="text-xs text-gray-500">
                        Showing {historyRows.length} of{" "}
                        {kpiHistoryData?.pagination.totalRecords ?? 0} account
                        officers (page {kpiHistoryData?.pagination.page ?? kpiHistoryPage} of{" "}
                        {kpiHistoryData?.pagination.totalPages ?? 1})
                      </p>
                      <PageNumberPagination
                        currentPage={kpiHistoryData?.pagination.page ?? kpiHistoryPage}
                        totalPages={kpiHistoryData?.pagination.totalPages ?? 1}
                        onPageChange={setKpiHistoryPage}
                        disabled={kpiHistoryLoading}
                      />
                    </div>

                    <button
                      type="button"
                      onClick={() => handleExportKpiHistory("")}
                      disabled={kpiHistoryExporting || kpiHistoryLoading}
                      className="self-end m-4 rounded-lg border border-[#008060] bg-white px-3 py-1.5 text-sm font-medium text-[#008060] hover:bg-[#eaf7f3] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {kpiHistoryExporting ? "Exporting..." : "Export to Excel"}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </section>

      {showReassignModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl w-full max-w-md p-6 shadow-xl animate-in fade-in zoom-in duration-200">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-lg font-bold text-gray-900">
                Reassign Customer
              </h3>
              <button
                onClick={() => setShowReassignModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <IoMdClose size={24} />
              </button>
            </div>
            <p className="text-gray-500 text-sm mb-6">
              Select a new Account Officer for this customer
            </p>

            <div className="mb-8">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select New Account Officer
              </label>
              <div className="relative">
                <select
                  value={selectedOfficerId}
                  onChange={(e) => setSelectedOfficerId(e.target.value)}
                  className="w-full appearance-none bg-gray-50 border border-gray-100 rounded-lg py-3 px-4 text-sm text-gray-600 outline-none focus:ring-1 focus:ring-[#008060]"
                >
                  <option value="">Select Account Officer</option>
                  {officers.map((officer: any) => (
                    <option key={officer.staffId} value={officer.staffId}>
                      {officer.name}
                    </option>
                  ))}
                </select>
                <div className="absolute inset-y-0 right-0 flex items-center px-4 pointer-events-none text-gray-500">
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowReassignModal(false)}
                className="flex-1 py-2.5 border border-gray-200 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleReassignCustomer}
                disabled={!selectedOfficerId || assignmentLoading}
                className="flex-1 py-2.5 bg-[#008060] rounded-lg text-sm font-medium text-white hover:bg-[#006048] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {assignmentLoading ? "Reassigning..." : "Reassign"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showMigrateModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl w-full max-w-md p-6 shadow-xl animate-in fade-in zoom-in duration-200">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-lg font-bold text-gray-900">
                Bulk Customer Migration
              </h3>
              <button
                onClick={() => !migrationLoading && setShowMigrateModal(false)}
                disabled={migrationLoading}
                className="text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <IoMdClose size={24} />
              </button>
            </div>
            <p className="text-gray-500 text-sm mb-6">
              Transfer all customers from one Account Officer to another
            </p>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                From (Source Account Officer)
              </label>
              <div className="relative">
                <select
                  value={sourceOfficerId}
                  onChange={(e) => setSourceOfficerId(e.target.value)}
                  disabled={migrationLoading}
                  className="w-full appearance-none bg-gray-50 border border-gray-100 rounded-lg py-3 px-4 text-sm text-gray-600 outline-none focus:ring-1 focus:ring-[#008060] disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  <option value="">Select Account Officer</option>
                  {officers.map((officer: any) => (
                    <option key={officer.staffId} value={officer.staffId}>
                      {officer.name}
                    </option>
                  ))}
                </select>
                <div className="absolute inset-y-0 right-0 flex items-center px-4 pointer-events-none text-gray-500">
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </div>
              </div>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                To (Target Account Officer)
              </label>
              <div className="relative">
                <select
                  value={targetOfficerId}
                  onChange={(e) => setTargetOfficerId(e.target.value)}
                  disabled={migrationLoading}
                  className="w-full appearance-none bg-gray-50 border border-gray-100 rounded-lg py-3 px-4 text-sm text-gray-600 outline-none focus:ring-1 focus:ring-[#008060] disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  <option value="">Select Account Officer</option>
                  {officers
                    .filter((o: any) => o.staffId !== sourceOfficerId)
                    .map((officer: any) => (
                      <option key={officer.staffId} value={officer.staffId}>
                        {officer.name}
                      </option>
                    ))}
                </select>
                <div className="absolute inset-y-0 right-0 flex items-center px-4 pointer-events-none text-gray-500">
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </div>
              </div>
            </div>

            {migrationLoading && migrationProgressText && (
              <div className="mb-6 p-4 bg-gray-50 border border-gray-100 rounded-lg flex flex-col gap-2">
                <div className="flex items-center gap-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#008060]"></div>
                  <span className="text-xs text-gray-700 font-semibold">
                    Migration in progress...
                  </span>
                </div>
                <p className="text-xs text-gray-500 font-mono">
                  {migrationProgressText}
                </p>
              </div>
            )}

            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowMigrateModal(false)}
                disabled={migrationLoading}
                className="flex-1 py-2.5 border border-gray-200 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  if (!sourceOfficerId || !targetOfficerId) return;
                  setMigrationLoading(true);
                  setMigrationProgressText("");
                  try {
                    const auth: any = sessionStorage.getItem("authState");
                    const jsonData = JSON.parse(auth || "{}");
                    const token = jsonData?.token;

                    const sourceOfficer = officers.find(
                      (o: any) => o.staffId === sourceOfficerId,
                    );
                    if (!sourceOfficer)
                      throw new Error("Source officer not found");

                    console.log(
                      "[Bulk Migrate] Source officer:",
                      sourceOfficer.name,
                      "staffId:",
                      sourceOfficer.staffId,
                    );

                    // Fetch ALL assigned customers across all pages to find source officer's customers
                    const response = await axios.get(
                      `${process.env.REACT_APP_API_URL}/staffs/assigned-customers`,
                      {
                        headers: { Authorization: `Bearer ${token}` },
                        params: { limit: 99999 },
                      },
                    );

                    const parsed = parseAssignedCustomersListResponse(
                      response.data,
                      response.headers,
                    );
                    const allCustomers = parsed.rows || [];

                    console.log(
                      "[Bulk Migrate] Total customers fetched:",
                      allCustomers.length,
                    );

                    // Match customers belonging to source officer by name or ID
                    const officerName = (sourceOfficer.name || "")
                      .trim()
                      .toLowerCase();
                    const customersToMigrate = allCustomers
                      .filter((u: any) => {
                        // Match by accountOfficer name
                        const nameMatch =
                          u.accountOfficer &&
                          officerName &&
                          u.accountOfficer.trim().toLowerCase() === officerName;
                        // Match by any officer ID field
                        const idMatch =
                          (u.officerId && u.officerId === sourceOfficerId) ||
                          (u.accountOfficerId &&
                            u.accountOfficerId === sourceOfficerId) ||
                          (u.staffId && u.staffId === sourceOfficerId);
                        return nameMatch || idMatch;
                      })
                      .map((u: any) => u.customerId);

                    console.log(
                      "[Bulk Migrate] Customers matched for source officer:",
                      customersToMigrate.length,
                    );

                    if (customersToMigrate.length === 0) {
                      toast.error("No customers found for source officer.");
                      setMigrationLoading(false);
                      return;
                    }

                    const customerEmails = customersToMigrate.map((id) => {
                      const user = allCustomers.find(
                        (u: any) => u.customerId === id,
                      );
                      return user?.customerEmail || "";
                    });

                    const totalUsers = customersToMigrate.length;
                    const BATCH_SIZE = 50;
                    let successCount = 0;

                    for (let i = 0; i < totalUsers; i += BATCH_SIZE) {
                      const chunkIds = customersToMigrate.slice(
                        i,
                        i + BATCH_SIZE,
                      );
                      const chunkEmails = customerEmails.slice(
                        i,
                        i + BATCH_SIZE,
                      );

                      const currentBatchNumber = Math.floor(i / BATCH_SIZE) + 1;
                      const totalBatches = Math.ceil(totalUsers / BATCH_SIZE);

                      setMigrationProgressText(
                        `Migrating batch ${currentBatchNumber} of ${totalBatches} (${Math.min(i + BATCH_SIZE, totalUsers)} of ${totalUsers} customers)...`,
                      );

                      console.log(
                        `[Bulk Migrate] Calling bulk-assign for batch ${currentBatchNumber}/${totalBatches} with`,
                        chunkIds.length,
                        "customers",
                      );

                      await axios.post(
                        `${process.env.REACT_APP_API_URL}/staffs/bulk-assign`,
                        {
                          officerId: targetOfficerId,
                          customerIds: chunkIds,
                          customerEmails: chunkEmails,
                        },
                        {
                          headers: {
                            Authorization: `Bearer ${token}`,
                          },
                        },
                      );

                      successCount += chunkIds.length;
                    }

                    setShowMigrateModal(false);
                    setSourceOfficerId("");
                    setTargetOfficerId("");
                    fetchAllUsers(1, debouncedAllUsersSearch);
                    fetchOfficers();
                    fetchStats();
                    fetchPendingCustomers(1, debouncedPendingSearch);
                    toast.success(
                      `Successfully migrated ${successCount} customers.`,
                    );
                  } catch (error: any) {
                    console.error("Migration error:", error);
                    toast.error(
                      error.response?.data?.message ||
                        error.message ||
                        "Failed to migrate",
                    );
                    // Refresh even on error so that completed batches are shown correctly
                    fetchAllUsers(1, debouncedAllUsersSearch);
                    fetchOfficers();
                    fetchStats();
                    fetchPendingCustomers(1, debouncedPendingSearch);
                  } finally {
                    setMigrationLoading(false);
                    setMigrationProgressText("");
                  }
                }}
                disabled={
                  !sourceOfficerId || !targetOfficerId || migrationLoading
                }
                className="flex-1 py-2.5 bg-[#008060] rounded-lg text-sm font-medium text-white hover:bg-[#006048] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {migrationLoading ? "Migrating..." : "Migrate All"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showAssignModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl w-full max-w-md p-6 shadow-xl animate-in fade-in zoom-in duration-200">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-lg font-bold text-gray-900">
                Assign Account Officer to User
              </h3>
              <button
                onClick={() => setShowAssignModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <IoMdClose size={24} />
              </button>
            </div>
            <p className="text-gray-500 text-sm mb-6">
              Select a new Account Officer for this customer
            </p>

            <div className="mb-8">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select New Account Officer
              </label>
              <div className="relative">
                <select
                  value={selectedOfficerId}
                  onChange={(e) => setSelectedOfficerId(e.target.value)}
                  className="w-full appearance-none bg-gray-50 border border-gray-100 rounded-lg py-3 px-4 text-sm text-gray-600 outline-none focus:ring-1 focus:ring-[#008060]"
                >
                  <option value="">Select Account Officer</option>
                  {officers.map((officer: any) => (
                    <option key={officer.staffId} value={officer.staffId}>
                      {officer.name}
                    </option>
                  ))}
                </select>
                <div className="absolute inset-y-0 right-0 flex items-center px-4 pointer-events-none text-gray-500">
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowAssignModal(false)}
                className="flex-1 py-2.5 border border-gray-200 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleAssignCustomer}
                disabled={!selectedOfficerId || assignmentLoading}
                className="flex-1 py-2.5 bg-[#008060] rounded-lg text-sm font-medium text-white hover:bg-[#006048] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {assignmentLoading ? "Assigning..." : "Assign"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showBulkAssignModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl w-full max-w-md p-6 shadow-xl animate-in fade-in zoom-in duration-200">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-lg font-bold text-gray-900">
                Bulk Customer Assignment
              </h3>
              <button
                onClick={() =>
                  !bulkAssignmentLoading && setShowBulkAssignModal(false)
                }
                disabled={bulkAssignmentLoading}
                className="text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <IoMdClose size={24} />
              </button>
            </div>
            <p className="text-gray-500 text-sm mb-6">
              Assign an Account Officer to the selected{" "}
              {selectedPendingUsers.length} customers
            </p>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Account Officer
              </label>
              <div className="relative">
                <select
                  value={bulkSelectedOfficerId}
                  onChange={(e) => setBulkSelectedOfficerId(e.target.value)}
                  disabled={bulkAssignmentLoading}
                  className="w-full appearance-none bg-gray-50 border border-gray-100 rounded-lg py-3 px-4 text-sm text-gray-600 outline-none focus:ring-1 focus:ring-[#008060] disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  <option value="">Select Account Officer</option>
                  {officers.map((officer: any) => (
                    <option key={officer.staffId} value={officer.staffId}>
                      {officer.name}
                    </option>
                  ))}
                </select>
                <div className="absolute inset-y-0 right-0 flex items-center px-4 pointer-events-none text-gray-500">
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </div>
              </div>
            </div>

            {bulkAssignmentLoading && bulkAssignProgressText && (
              <div className="mb-6 p-4 bg-gray-50 border border-gray-100 rounded-lg flex flex-col gap-2">
                <div className="flex items-center gap-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#008060]"></div>
                  <span className="text-xs text-gray-700 font-semibold">
                    Assignment in progress...
                  </span>
                </div>
                <p className="text-xs text-gray-500 font-mono">
                  {bulkAssignProgressText}
                </p>
              </div>
            )}

            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowBulkAssignModal(false)}
                disabled={bulkAssignmentLoading}
                className="flex-1 py-2.5 border border-gray-200 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={handleBulkAssign}
                disabled={
                  !bulkSelectedOfficerId ||
                  bulkAssignmentLoading ||
                  selectedPendingUsers.length === 0
                }
                className="flex-1 py-2.5 bg-[#008060] rounded-lg text-sm font-medium text-white hover:bg-[#006048] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {bulkAssignmentLoading ? "Assigning..." : "Assign All"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showAddOfficerModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl w-full max-w-md p-6 shadow-xl animate-in fade-in zoom-in duration-200 overflow-y-auto max-h-[90vh]">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-lg font-bold text-gray-900">
                Add New Account Officer
              </h3>
              <button
                onClick={() => setShowAddOfficerModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <IoMdClose size={24} />
              </button>
            </div>
            <p className="text-gray-500 text-sm mb-6">
              Enter new Account Officer details
            </p>

            <div className="space-y-4 mb-8">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Add Name
                </label>
                <input
                  type="text"
                  placeholder="Enter Full Name"
                  className="w-full bg-gray-50 border-none rounded-lg py-3 px-4 text-sm focus:ring-1 focus:ring-[#008060] outline-none placeholder-gray-400"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  E-mail Address
                </label>
                <input
                  type="email"
                  placeholder="Enter Email Address"
                  className="w-full bg-gray-50 border-none rounded-lg py-3 px-4 text-sm focus:ring-1 focus:ring-[#008060] outline-none placeholder-gray-400"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Phone Number
                </label>
                <input
                  type="tel"
                  placeholder="Enter Phone Number"
                  className="w-full bg-gray-50 border-none rounded-lg py-3 px-4 text-sm focus:ring-1 focus:ring-[#008060] outline-none placeholder-gray-400"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Status on Creation
                </label>
                <div className="relative">
                  <select className="w-full appearance-none bg-gray-50 border-none rounded-lg py-3 px-4 text-sm text-gray-600 outline-none focus:ring-1 focus:ring-[#008060]">
                    <option>Active</option>
                    <option>Inactive</option>
                  </select>
                  <div className="absolute inset-y-0 right-0 flex items-center px-4 pointer-events-none text-gray-500">
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Referral Code
                </label>
                <input
                  type="text"
                  placeholder="Enter Referral Code"
                  className="w-full bg-gray-50 border-none rounded-lg py-3 px-4 text-sm focus:ring-1 focus:ring-[#008060] outline-none placeholder-gray-400"
                />
              </div>
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowAddOfficerModal(false)}
                className="flex-1 py-2.5 border border-gray-200 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => setShowAddOfficerModal(false)}
                className="flex-1 py-2.5 bg-[#008060] rounded-lg text-sm font-medium text-white hover:bg-[#006048] transition-colors"
              >
                Assign
              </button>
            </div>
          </div>
        </div>
      )}

      {showOrderHistoryModal && selectedOrderHistoryUser && (
        <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-sm overflow-hidden shadow-2xl animate-in fade-in zoom-in duration-200">
            <div className="bg-white border-b border-gray-100 flex items-center justify-between p-4 px-6">
              <div>
                <h3 className="text-lg font-bold text-gray-900">
                  Order History
                </h3>
                <p className="text-xs font-medium text-gray-500 mt-0.5">
                  {selectedOrderHistoryUser.customerName || "Customer"}
                </p>
              </div>
              <button
                onClick={() => setShowOrderHistoryModal(false)}
                className="flex items-center justify-center w-8 h-8 rounded-full bg-gray-50 text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
              >
                <IoMdClose size={18} />
              </button>
            </div>

            <div
              className="p-4 px-6 max-h-[50vh] overflow-y-auto custom-scrollbar"
              style={{ scrollbarWidth: "none" }}
            >
              {orderHistoryLoading ? (
                <div className="flex items-center justify-center py-10">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#008060]"></div>
                  <span className="ml-3 text-sm text-gray-500">
                    Loading orders...
                  </span>
                </div>
              ) : orderHistoryData.length === 0 ? (
                <div className="text-center py-10">
                  <div className="w-14 h-14 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-4 border border-gray-100">
                    <svg
                      className="w-6 h-6 text-gray-300"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.5}
                        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  </div>
                  <p className="text-sm font-semibold text-gray-800">
                    No orders yet
                  </p>
                  <p className="text-xs text-gray-500 mt-1.5">
                    This user hasn't placed any orders.
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {orderHistoryData.map((order: any, i: number) => {
                    const orderDate =
                      order.createdAt || order.date || order.orderDate;
                    const dateObj = orderDate ? new Date(orderDate) : null;
                    return (
                      <div
                        key={order._id || order.orderId || i}
                        className="flex items-center justify-between p-3.5 rounded-xl border border-gray-100 hover:border-[#008060]/20 hover:bg-[#008060]/[0.02] hover:shadow-sm transition-all group"
                      >
                        <div className="flex items-center gap-3.5">
                          <div className="w-10 h-10 rounded-full bg-[#008060]/10 flex items-center justify-center text-[#008060]">
                            <svg
                              className="w-5 h-5 flex-shrink-0"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={1.5}
                                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                              />
                            </svg>
                          </div>
                          <div>
                            <p className="text-sm font-semibold text-gray-800">
                              {dateObj
                                ? dateObj.toLocaleDateString("en-GB", {
                                    day: "numeric",
                                    month: "short",
                                    year: "numeric",
                                  })
                                : "N/A"}
                            </p>
                            <p className="text-xs font-medium text-gray-500 mt-0.5">
                              {dateObj
                                ? dateObj.toLocaleTimeString("en-US", {
                                    hour: "2-digit",
                                    minute: "2-digit",
                                    hour12: true,
                                  })
                                : ""}
                            </p>
                          </div>
                        </div>
                        <div className="w-2 h-2 rounded-full bg-[#008060]/40 group-hover:bg-[#008060] transition-colors"></div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {showOfficerDetailsModal && selectedOfficerDetails && (
        <div
          className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
          onClick={() => setShowOfficerDetailsModal(false)}
        >
          <div
            className="bg-white rounded-2xl w-full max-w-2xl shadow-xl animate-in fade-in zoom-in duration-200 overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-gradient-to-br from-[#008060] to-[#009e75] px-8 py-8 text-white relative flex items-center gap-5">
              <button
                onClick={() => setShowOfficerDetailsModal(false)}
                className="absolute top-5 right-5 w-8 h-8 flex items-center justify-center rounded-full bg-black/10 text-white/90 hover:bg-black/20 hover:text-white transition-all backdrop-blur-sm"
              >
                <IoMdClose size={18} />
              </button>
              <div className="w-16 h-16 rounded-2xl bg-white/20 backdrop-blur-md flex items-center justify-center text-white text-2xl font-bold shadow-sm border border-white/10">
                {selectedOfficerDetails.name?.charAt(0)?.toUpperCase() || "A"}
              </div>
              <div>
                <h3 className="text-xl font-bold tracking-tight mb-0.5">
                  {selectedOfficerDetails.name}
                </h3>
                <p className="text-[#e6f5ee] text-sm font-medium mb-2 opacity-90">
                  {selectedOfficerDetails.officialEmail ||
                    selectedOfficerDetails.email}
                </p>
                <span
                  className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider ${selectedOfficerDetails.status === "active" ? "bg-white/20 text-white backdrop-blur-sm" : "bg-red-500/80 text-white backdrop-blur-sm"}`}
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-current"></span>
                  {selectedOfficerDetails.status}
                </span>
              </div>
            </div>

            <div
              className="overflow-y-auto max-h-[65vh] bg-gray-50/30"
              style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
            >
              <div className="px-8 py-8">
                <div className="grid grid-cols-2 gap-4 mb-10">
                  <div className="bg-white border border-gray-100 rounded-2xl p-5 text-center shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-300 ease-out cursor-default">
                    <p className="text-3xl font-extrabold text-gray-900 tracking-tight mb-1">
                      {selectedOfficerDetails.assignedCustomersCount || 0}
                    </p>
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Total Customers
                    </p>
                  </div>
                  <div className="bg-white border border-gray-100 rounded-2xl p-5 text-center shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-300 ease-out cursor-default">
                    <p className="text-3xl font-extrabold text-gray-900 tracking-tight mb-1">
                      ₦
                      {(
                        selectedOfficerDetails.totalRevenue || 0
                      ).toLocaleString()}
                    </p>
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Total Revenue
                    </p>
                  </div>
                  <div className="bg-[#008060]/5 border border-[#008060]/10 rounded-2xl p-5 text-center shadow-sm hover:shadow-md hover:bg-[#008060]/10 hover:-translate-y-0.5 transition-all duration-300 ease-out cursor-default">
                    <p className="text-3xl font-extrabold text-[#008060] tracking-tight mb-1">
                      {selectedOfficerDetails.kpi?.totalOrders ??
                        selectedOfficerDetails.totalOrdersThisMonth ??
                        0}
                    </p>
                    <p className="text-xs font-semibold text-[#008060]/80 uppercase tracking-wider">
                      Orders This Month
                    </p>
                  </div>
                  <div className="bg-white border border-gray-100 rounded-2xl p-5 text-center shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-300 ease-out cursor-default">
                    <p className="text-3xl font-extrabold text-gray-900 tracking-tight mb-1">
                      {(() => {
                        const v =
                          selectedOfficerDetails.kpi?.conversionRate ??
                          selectedOfficerDetails.conversionRate;
                        if (v == null || v === "") return "0%";
                        return `${v}%`;
                      })()}
                    </p>
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Monthly Conv. Rate
                    </p>
                  </div>
                </div>

                <div className="mb-10">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-5">
                    <h4 className="text-sm font-bold text-gray-900 uppercase tracking-wider">
                      Monthly KPI Targets
                    </h4>
                    <div className="flex items-center gap-2">
                      <div className="w-32">
                        <CustomSelect
                          value={modalKpiMonth}
                          onChange={setModalKpiMonth}
                          options={officerDetailsMonths.map((m) => ({
                            label: m,
                            value: m,
                          }))}
                        />
                      </div>
                      <div className="w-24">
                        <CustomSelect
                          value={modalKpiYear}
                          onChange={(val) => {
                            setModalKpiYear(val);
                            if (
                              val === currentYear &&
                              months.indexOf(modalKpiMonth) > today.getMonth()
                            ) {
                              setModalKpiMonth(currentMonth);
                            }
                          }}
                          options={officerDetailsYears.map((y) => ({
                            label: y,
                            value: y,
                          }))}
                        />
                      </div>
                    </div>
                  </div>
                  {officerKpiLoading ? (
                    <div className="flex flex-col items-center justify-center py-10">
                      <div className="w-8 h-8 border-2 border-gray-200 border-t-[#008060] rounded-full animate-spin mb-4"></div>
                      <p className="text-sm text-gray-500 font-medium">
                        Loading KPI targets...
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <div className="bg-white border border-gray-100 rounded-2xl p-4 flex items-center justify-between shadow-sm hover:shadow-md transition-shadow duration-300 cursor-default">
                        <div>
                          <p className="text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-1">
                            Target Revenue
                          </p>
                          <p className="text-lg font-bold text-gray-900">
                            {selectedOfficerDetails.kpi?.targetRevenue
                              ? `₦${selectedOfficerDetails.kpi.targetRevenue.toLocaleString()}`
                              : "Not Set"}
                          </p>
                        </div>
                        <div className="flex items-center gap-3">
                          <p className="text-xs font-semibold text-gray-400">
                            Achieved:
                          </p>
                          {(() => {
                            const isCurrentPeriod =
                              modalKpiMonth === currentMonth &&
                              modalKpiYear === currentYear;
                            const val =
                              selectedOfficerDetails.kpi?.revenue ??
                              (isCurrentPeriod
                                ? selectedOfficerDetails.revenueThisMonth || 0
                                : 0);
                            return (
                              <div
                                className={`px-3 py-1 rounded-lg text-sm font-bold ${val > 0 ? "bg-[#008060]/10 text-[#008060] border border-[#008060]/20" : "bg-gray-100 text-gray-500 border border-gray-200"}`}
                              >
                                ₦{val.toLocaleString()}
                              </div>
                            );
                          })()}
                        </div>
                      </div>
                      <div className="bg-white border border-gray-100 rounded-2xl p-4 flex items-center justify-between shadow-sm hover:shadow-md transition-shadow duration-300 cursor-default">
                        <div>
                          <p className="text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-1">
                            Target Total Orders
                          </p>
                          <p className="text-lg font-bold text-gray-900">
                            {selectedOfficerDetails.kpi?.totalExpectedOrders
                              ? selectedOfficerDetails.kpi.totalExpectedOrders.toLocaleString()
                              : "Not Set"}
                          </p>
                        </div>
                        <div className="flex items-center gap-3">
                          <p className="text-xs font-semibold text-gray-400">
                            Achieved:
                          </p>
                          {(() => {
                            const isCurrentPeriod =
                              modalKpiMonth === currentMonth &&
                              modalKpiYear === currentYear;
                            const val =
                              selectedOfficerDetails.kpi?.totalOrders ??
                              (isCurrentPeriod
                                ? selectedOfficerDetails.totalOrdersThisMonth ||
                                  0
                                : 0);
                            return (
                              <div
                                className={`px-3 py-1 rounded-lg text-sm font-bold ${val > 0 ? "bg-[#008060]/10 text-[#008060] border border-[#008060]/20" : "bg-gray-100 text-gray-500 border border-gray-200"}`}
                              >
                                {val}
                              </div>
                            );
                          })()}
                        </div>
                      </div>
                      <div className="bg-white border border-gray-100 rounded-2xl p-4 flex items-center justify-between shadow-sm hover:shadow-md transition-shadow duration-300 cursor-default">
                        <div>
                          <p className="text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-1">
                            Target Total Customers
                          </p>
                          <p className="text-lg font-bold text-gray-900">
                            {selectedOfficerDetails.kpi
                              ?.expectedUserRegistrations
                              ? selectedOfficerDetails.kpi.expectedUserRegistrations.toLocaleString()
                              : "Not Set"}
                          </p>
                        </div>
                        <div className="flex items-center gap-3">
                          <p className="text-xs font-semibold text-gray-400">
                            Achieved:
                          </p>
                          {(() => {
                            const isCurrentPeriod =
                              modalKpiMonth === currentMonth &&
                              modalKpiYear === currentYear;
                            const val =
                              selectedOfficerDetails.kpi?.newRegistrations ??
                              (isCurrentPeriod
                                ? selectedOfficerDetails.assignedCustomersCount ||
                                  0
                                : 0);
                            return (
                              <div
                                className={`px-3 py-1 rounded-lg text-sm font-bold ${val > 0 ? "bg-[#008060]/10 text-[#008060] border border-[#008060]/20" : "bg-gray-100 text-gray-500 border border-gray-200"}`}
                              >
                                {val}
                              </div>
                            );
                          })()}
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div>
                  <h4 className="text-sm font-bold text-gray-900 mb-4 uppercase tracking-wider">
                    Contact Information
                  </h4>
                  <div className="bg-white rounded-2xl border border-gray-100 p-1 flex flex-col shadow-sm">
                    <div className="flex items-center justify-between p-4 border-b border-gray-50 last:border-0 hover:bg-gray-50/50 transition-colors duration-200 rounded-t-xl group">
                      <span className="text-sm font-medium text-gray-500">
                        Phone Number
                      </span>
                      <div className="flex items-center gap-4">
                        <span className="text-sm font-bold text-gray-900">
                          {selectedOfficerDetails.phoneNumber || "N/A"}
                        </span>
                        {selectedOfficerDetails.phoneNumber && (
                          <div className="flex items-center gap-1.5 transition-opacity">
                            <button
                              onClick={() =>
                                copyToClipboard(
                                  selectedOfficerDetails.phoneNumber,
                                  "Phone Number",
                                )
                              }
                              className="p-1.5 text-gray-400 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors"
                              title="Copy"
                            >
                              <svg
                                className="w-4 h-4"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                                />
                              </svg>
                            </button>
                            <a
                              href={`https://wa.me/${selectedOfficerDetails.phoneNumber?.replace(/^0/, "234").replace(/[^0-9]/g, "")}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="p-1.5 text-[#25D366] hover:bg-[#25D366]/10 rounded-md transition-colors"
                              title="Chat on WhatsApp"
                            >
                              <FaWhatsapp size={16} />
                            </a>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center justify-between p-4 border-b border-gray-50 last:border-0 hover:bg-gray-50/50 transition-colors duration-200 group">
                      <span className="text-sm font-medium text-gray-500">
                        Email Address
                      </span>
                      <div className="flex items-center gap-4">
                        <span className="text-sm font-bold text-gray-900">
                          {selectedOfficerDetails.officialEmail ||
                            selectedOfficerDetails.email ||
                            "N/A"}
                        </span>
                        {(selectedOfficerDetails.officialEmail ||
                          selectedOfficerDetails.email) && (
                          <div className="flex items-center gap-1.5 transition-opacity">
                            <button
                              onClick={() =>
                                copyToClipboard(
                                  selectedOfficerDetails.officialEmail ||
                                    selectedOfficerDetails.email,
                                  "Email",
                                )
                              }
                              className="p-1.5 text-gray-400 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors"
                              title="Copy"
                            >
                              <svg
                                className="w-4 h-4"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                                />
                              </svg>
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center justify-between p-4 hover:bg-gray-50/50 transition-colors duration-200 group">
                      <span className="text-sm font-medium text-gray-500">
                        Referral Code
                      </span>
                      <div className="flex items-center gap-4">
                        <span className="bg-gray-50 border border-gray-200 text-gray-800 px-2 py-0.5 rounded text-sm font-mono font-medium">
                          {selectedOfficerDetails.referralCode || "N/A"}
                        </span>
                        {selectedOfficerDetails.referralCode && (
                          <div className="flex items-center gap-1.5 transition-opacity">
                            <button
                              onClick={() =>
                                copyToClipboard(
                                  selectedOfficerDetails.referralCode,
                                  "Referral Code",
                                )
                              }
                              className="p-1.5 text-gray-400 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors"
                              title="Copy"
                            >
                              <svg
                                className="w-4 h-4"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                                />
                              </svg>
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center justify-between mt-4 px-4 py-4 bg-[#008060]/5 rounded-xl border border-[#008060]/10">
                    <span className="text-sm font-bold text-[#008060] uppercase tracking-wider">
                      Total Orders Lifetime
                    </span>
                    <span className="text-xl font-extrabold text-[#008060]">
                      {selectedOfficerDetails.totalOrders ||
                        selectedOfficerDetails.assignedCustomersCount ||
                        0}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className="px-8 py-5 bg-white border-t border-gray-100 flex justify-end">
              <button
                onClick={() => setShowOfficerDetailsModal(false)}
                className="px-6 py-2.5 bg-gray-900 text-white rounded-xl text-sm font-bold hover:bg-gray-800 hover:shadow-md transition-all duration-200 active:scale-[0.98]"
              >
                Close View
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
};

export default AccountManagement;
